import { HttpErrorResponse } from "@angular/common/http";
import { Subscription } from "rxjs";
import { Compass, CompassAccessControl, CompassAnswerable, CompassAnswerableButton, CompassAnswerableButtonWeight, CompassAnswerableReminder, CompassAnswerableType, CompassAssessment, CompassCopyControl, CompassPublicationType, CompassScaleRange, CompassScoreGroup, CompassStatus, CompassTrackable, CompassTrackableIntegrationType, CompassTrackableIntegrationTypeUtil, CompassTrackableScaleType, DeleteCompass, Document, LicenseTier, Multisaveable, OntologyService, Provider, SaveCompass, TrackableScaleRange, TrackableValuation } from "huddyapi";
import { AbstractSumDependency, ContributionGroupAggregationType, CountUpdators, Dependency, LineRange, MapCountDependency, Range, RangeOverlapDependency, SavedField, SavedGroupStatus, SavedMultiselectField, SavedNumberField, SavedObject, SavedAutocompleteField, SavedSelectField, SavedTextField, SelectOption, SelectOptions, SelectOptionsChange, SimpleDependency, SimpleStatusField, SquareRange, SumContributionGroupsDependency, SumContributionsDependency, SumDependency, ValueDependency, SimpleSavedField } from "../components/multisave";
import { PublishCompassDialog } from "../components/publish-compass-dialog/publish-compass-dialog.component";
import { DesktopErrorHandler } from "../services/errorHandler.service";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar as MatSnackBar } from "@angular/material/snack-bar";
import { Store } from "@ngxs/store";
import { UUIDService } from "../services/uuid.service";
import { ConfirmationDialog } from "../components/confirmation-dialog/confirmation-dialog.component";
import { ECSNode, ECStructure } from "../directives/collapsibleCard";
import { IntegrationTrackable } from "./builder.component";

type FieldMapFieldType = SavedField | SavedField[] | FieldMap | FieldMap[];
type FieldMapHandler = string | ((field: FieldMapFieldType, error: any) => void);
type FieldMapHandlers = { [code: string]: FieldMapHandler };
type FieldMapEntry = { name: string, handlers?: FieldMapHandlers };
type FieldMapType = { [savedName: string]: FieldMapEntry };

interface FieldMap {
  fieldMap: FieldMapType;
}

export interface Collapsible {
  expanded: boolean;
}

interface CompassEditable {
  getEditId(): number;
}

enum FieldMapHandlerStrings {
  DEFAULT = '__DEFAULT__',
  MESSAGE = '__MESSAGE__'
}

enum SpecialFieldName {
  THIS = '__THIS__'
}

export abstract class RangeID {
  static fromRangeIndex(index: number): string {
    const letters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ', length: number = letters.length;
    return (Math.floor(index / length) + 1) + letters.charAt(index % length);
  }
}

class SavedAccessControl extends SimpleDependency {
  private map: Map<CompassAccessControl, CompassCopyControl>;

  constructor(copyControls?: CompassCopyControl[], dependencies?: Dependency[]) {
    super(dependencies);
    this.setList(copyControls || []);
  }

  setList(copyControls: CompassCopyControl[]): void {
    this.map = new Map<CompassAccessControl, CompassCopyControl>((copyControls || []).map((control: CompassCopyControl) => [control.accessControl, control]));
    this.update();
  }

  getList(): CompassCopyControl[] {
    return Array.from(this.map.values());
  }

  has(accessControl: CompassAccessControl): boolean {
    return this.map.has(accessControl);
  }

  get(accessControl: CompassAccessControl): CompassCopyControl {
    return this.map.get(accessControl);
  }

  set(accessControl: CompassAccessControl, copyControl: CompassCopyControl): void {
    this.map.set(accessControl, copyControl);
    this.update();
  }
}

export class SavedCompass extends SavedObject<Compass> implements CompassEditable, FieldMap {
  objectName = 'compass';
  editing: number;
  status: CompassStatus;
  rejectReason: string;
  providerOptions: SelectOptions<string>;
  scoreGroupOptions: SelectOptions<string>;
  providerMap: Map<string, Provider>;
  isSelected: (compass: Compass | SavedCompass) => boolean;
  handleGroupChanged: () => void;
  handleBeforeGroupChanged: () => void;
  removeSavedCompasses: (savedCompasses: SavedCompass[]) => void;
  setAutocompleteTrackableFilterText: (value: string) => void;
  errorHandler: DesktopErrorHandler;
  snackbar: MatSnackBar;
  store: Store;
  ontologyService: OntologyService;
  subscriberCount: number;
  version: number;
  releaseVersion: number;
  publicationType: CompassPublicationType;
  versionControlId: number;
  sourceId: number;
  protectedSubscribers: string[];
  accessControl: SavedAccessControl;
  assessmentAccessControl: SavedAccessControl;
  licenseTiers: LicenseTier[];
  name: SavedTextField;
  providerName: SavedSelectField<string>;
  providerOptionsSubscription: Subscription;
  scoreName: SavedTextField;
  useCustomScoreClass: SavedSelectField<boolean>;
  useCustomScoreClassLinkDependency: Dependency;
  scoreClass: SavedTextField;
  conditionName: SavedAutocompleteField<ConditionSR>;
  description: SavedTextField;
  longDescription: SavedTextField;
  nameDependency: MapCountDependency<string>;
  scoreGroupNameDependency: MapCountDependency<string>;
  maxScoreDependency: SumContributionsDependency;
  assessmentFieldsDependency: SumDependency;
  trackables: CompassTrackableGroup[];
  savedTrackables: CompassTrackable[];
  trackableStatusField: SimpleStatusField;
  conclusionText: SavedTextField;
  attribution: SavedTextField;
  attributionLink: SavedTextField;
  showPreviousAnswers: SavedSelectField<boolean>;
  hasHuddyReminder: SavedSelectField<boolean>;
  reminderGroup: SavedGroupStatus;
  reminderDaysBetween: SavedNumberField;
  reminderPrompt: SavedTextField;
  reminderCaredForPrompt: SavedTextField;
  reminderButtonText: SavedTextField;
  answerablesGroup: SavedGroupStatus;
  answerables: CompassAnswerableGroup[];
  savedAnswerables: CompassAnswerable[];
  answerableStatusField: SimpleStatusField;
  scoreGroupsGroup: SavedGroupStatus;
  scoreGroups: CompassScoreGroupGroup[];
  savedScoreGroups: CompassScoreGroup[];
  scoreGroupStatusField: SimpleStatusField;
  scaleRangesGroup: SavedGroupStatus;
  scaleRanges: CompassScaleRangeGroup[];
  savedScaleRanges: CompassScaleRange[];
  documents: CompassDocumentsGroup[];
  savedDocuments: Document[];
  documentsStatusField: SimpleStatusField;
  rangesDependency: RangeOverlapDependency<LineRange>;
  rangeIdToIndexMap: Map<number, number>;
  rangesStatusField: SimpleStatusField;
  fieldMap: FieldMapType;
  maxGroup: number;
  scrollTops: number[];
  possibleIntegrationsMap: Map<string, CompassTrackableIntegrationType[]>;
  updateECState: () => void;

  constructor(from: Compass, parentUpdators: CountUpdators, providerOptions: SelectOptions<string>, providerMap: Map<string, Provider>, isSelected: (compass: Compass | SavedCompass) => boolean, handleGroupChanged: () => void, removeSavedCompasses: (savedCompasses: SavedCompass[]) => void, setAutocompleteTrackableFilterText: (value: string) => void, dialog: MatDialog, snackbar: MatSnackBar, store: Store, errorHandler: DesktopErrorHandler, ontologyService: OntologyService, handleBeforeGroupChanged: () => void, possibleIntegrationsMap: Map<string, CompassTrackableIntegrationType[]>, updateECState: () => void) {
    super(from, true, 6, parentUpdators, dialog);
    this.updateECState = updateECState;
    this.scrollTops = this.groups.map((): number => 0);
    this.possibleIntegrationsMap = possibleIntegrationsMap;
    this.maxGroup = from?.id > 0 ? null : 0;
    this.snackbar = snackbar;
    this.store = store;
    this.errorHandler = errorHandler;
    this.ontologyService = ontologyService
    this.providerOptions = providerOptions;
    this.providerMap = providerMap;
    this.isSelected = isSelected;
    this.handleGroupChanged = handleGroupChanged;
    this.handleBeforeGroupChanged = handleBeforeGroupChanged;
    this.removeSavedCompasses = removeSavedCompasses;
    this.setAutocompleteTrackableFilterText = setAutocompleteTrackableFilterText;
    this.status = from ? from.status : CompassStatus.DRAFT;
    this.rejectReason = from ? from.rejectReason : null;
    this.publicationType = from ? from.publicationType : CompassPublicationType.PRIVATE;
    this.versionControlId = from ? from.versionControlId : null;
    this.sourceId = from ? from.sourceId : null;
    this.protectedSubscribers = from ? from.protectedSubscribers : null;
    this.accessControl = new SavedAccessControl(from?.accessControl);
    this.assessmentAccessControl = new SavedAccessControl(from?.assessment?.accessControl);
    this.licenseTiers = from ? from.licenseTiers : null;
    this.rangeIdToIndexMap = new Map<number, number>();
    this.nameDependency = new MapCountDependency<string>();
    this.scoreGroupNameDependency = new MapCountDependency<string>();
    this.maxScoreDependency = new SumContributionsDependency();
    this.assessmentFieldsDependency = new SumDependency();
    this.rangesDependency = new RangeOverlapDependency();
    this.trackables = [];
    this.documents = [];
    this.savedDocuments = from?.documents || [];
    this.savedTrackables = from?.trackables || [];
    this.answerables = [];
    this.savedAnswerables = from?.assessment?.questions || [];
    this.scoreGroups = [];
    this.savedScoreGroups = this.getSavedScoreGroups(from);
    this.scoreGroupOptions = new SelectOptions<string>([], (a: SelectOption<string>, b: SelectOption<string>): number => 0);
    this.scaleRanges = [];
    this.savedScaleRanges = from?.scoreSummaryRanges || [];
    this.subscriberCount = from?.subscriberCount;
    this.version = from?.version;
    this.releaseVersion = from?.releaseVersion;
    const generalGroup: SavedGroupStatus = this.groups[0];
    const assessmentGroup: SavedGroupStatus = this.groups[2];
    this.reminderGroup = new SavedGroupStatus(assessmentGroup.updators);
    this.answerablesGroup = new SavedGroupStatus(assessmentGroup.updators);
    this.scoreGroupsGroup = new SavedGroupStatus(this.groups[3].updators);
    this.scaleRangesGroup = new SavedGroupStatus(this.groups[3].updators);
    this.useCustomScoreClassLinkDependency = new SimpleDependency();
    const reminderValidator: (field: SavedField) => string = (field: SavedField): string =>
      (this.hasReminder() && (field.value == null || field.value.length == 0)) ? 'Required' : '';
    this.trackableStatusField = new SimpleStatusField(this.groups[1].updators, null, [
      (): string => this.trackables.length == 0 ? 'Must provide at least one trackable' : ''
    ], (): boolean =>
      this.trackables.length != this.savedTrackables.length || this.trackables.findIndex((trackable: CompassTrackableGroup): boolean => trackable.isNew) != -1);
    this.documentsStatusField = new SimpleStatusField(this.groups[5].updators, null, [], (): boolean =>
      this.documents.length != this.savedDocuments.length || this.documents.findIndex((document: CompassDocumentsGroup): boolean => document.isNew) != -1);
    this.answerableStatusField = new SimpleStatusField(this.answerablesGroup.updators, [this.assessmentFieldsDependency, this.assessmentAccessControl], [
      (): string => (((!this.isOriginal() && this.assessmentAccessControl.has(CompassAccessControl.ASSESSMENT_DELETION_ALLOWED)) || this.assessmentFieldsDependency.getSum() > 0) && this.answerables.length == 0) ? 'Must provide at least one question' : ''
    ], (): boolean =>
      this.answerables.length != this.savedAnswerables.length || this.answerables.findIndex((answerable: CompassAnswerableGroup): boolean => answerable.isNew) != -1);
    this.scoreGroupStatusField = new SimpleStatusField(this.scoreGroupsGroup.updators, null, null, (): boolean =>
      this.scoreGroups.length != this.savedScoreGroups.length || this.scoreGroups.findIndex((scoreGroup: CompassScoreGroupGroup): boolean => scoreGroup.isNew()) != -1);
    this.rangesStatusField = new SimpleStatusField(this.scaleRangesGroup.updators, [this.maxScoreDependency, this.scoreGroupStatusField], [
      (): string => !this.usesCustomScoreClass() && this.scoreGroups.length > 1 && this.maxScoreDependency.hasContributors() && this.scaleRanges.length == 0 ? 'Must provide at least one scale range' : ''
    ], (): boolean =>
      this.scaleRanges.length != this.savedScaleRanges.length || this.scaleRanges.findIndex((scaleRange: CompassScaleRangeGroup): boolean => scaleRange.isNew) != -1);
    this.scoreGroupOptions.optionsChanged.subscribe((change: SelectOptionsChange<string>) => {
      const wasMulti: boolean = change.lastOptions.length > 1, isMulti: boolean = change.options.length > 1;
      if (isMulti != wasMulti) {
        this.clearScaleRanges();
        if (isMulti)
          this.setScaleRanges(this.savedScaleRanges)
      }
    });
    const isEditable: () => boolean = (): boolean => this.isEditing(this);
    this.name = new SavedTextField(from ? from.name : null, null, generalGroup.updators, null, null, isEditable, null, true, null, 128);
    this.providerName = new SavedSelectField<string>(from ? from.providerName : null, null, generalGroup.updators, null, null,
      true, null, null, this.providerOptions, isEditable);
    this.providerOptionsSubscription = this.providerOptions.optionsChanged.subscribe((change: SelectOptionsChange<string>) => {
      if (this.providerName.value != null && (this.isNew() || this.isDirty() || this.isSelected(this)) && change.options.findIndex((option: SelectOption<string>): boolean => option.value == this.providerName.value) == -1)
        this.providerName.value = null;
    });
    this.scoreName = new SavedTextField(from ? from.scoreName : null, null, generalGroup.updators, null, null, isEditable, null, true,
      null, 50);
    this.conditionName = new SavedAutocompleteField<ConditionSR>((term: string): ConditionSR[] | Promise<ConditionSR[]> => {
      return this.ontologyService.searchConditions(term).toPromise() as Promise<ConditionSR[]>;
    }, (result: ConditionSR): string => result.name, (value: ConditionSR): void => {
      if (value.id < 1)
        return;
      this.dialog.open(ConfirmationDialog, {
        width: '636px',
        data: {
          minWidth: '300px',
          title: 'Autofill trackables?',
          body: 'Would you like to use the trackables for ' + value.name + '?',
          buttons: [
            {
              text: 'Cancel',
              class: 'secondary',
              handler: () => null
            },
            {
              text: 'Autofill',
              class: '',
              handler: (): void | Promise<void> => {
                return this.ontologyService.getSymptomsForCondition(value.id).toPromise().then((result: any) => {
                  this.setTrackables(result?.symptoms?.map((symptom: string): CompassTrackable => new CompassTrackable({
                    name: symptom,
                    trackableType: 'SYMPTOM'
                  })) || [], true);
                  this.updateECState();
                }).catch((error: any) => {
                  this.errorHandler.handle(error, 'symptoms');
                });
              }
            }
          ]
        }
      });
    }, from ? from.conditionName : null, null, generalGroup.updators, null, null, isEditable, null, true, null, 255);
    const scoreClassIsEditable: () => boolean = (): boolean => {
      if (!isEditable())
        return false;
      if (this.isOriginal())
        return true;
      if (this.accessControl.has(CompassAccessControl.SCORE_SUMMARY_TEXT_EDITING_ALLOWED) || this.accessControl.has(CompassAccessControl.SCORE_SUMMARY_SCALE_EDITING_ALLOWED))
        return false;
      for (let trackable of this.trackables) {
        if (trackable.accessControl.has(CompassAccessControl.TRACKABLE_SCALE_EDITING_ALLOWED))
          return false;
      }
      for (let answerable of this.answerables) {
        if (answerable.accessControl.has(CompassAccessControl.ASSESSMENT_QUESTION_SCALE_EDITING_ALLOWED))
          return false;
      }
      return true;
    };
    this.useCustomScoreClass = new SavedSelectField<boolean>(from ? from.scoreClass != Compass.STANDARD_SCORE_CLASS : false, null,
      generalGroup.updators, [this.accessControl, this.trackableStatusField, this.answerableStatusField], null, true, null, null, new SelectOptions<boolean>([
        { value: true, displayValue: 'Yes' }, { value: false, displayValue: 'No' }
      ], (a: SelectOption<boolean>, b: SelectOption<boolean>): number => (b.value ? 1 : 0) - (a.value ? 1 : 0)), scoreClassIsEditable,
      (oldValue: boolean, newValue: boolean): void => {
        if (oldValue != newValue) {
          if (newValue) {
            this.scoreClass?.savedValue == Compass.STANDARD_SCORE_CLASS ? this.scoreClass.value = null : this.scoreClass?.reset();
            if (this.groupIndex == 3)
              this.groupIndex = 2;
            this.clearScaleRanges();
            this.answerables?.forEach((answerable: CompassAnswerableGroup) => {
              answerable.buttons?.forEach((button: CompassAnswerableButtonGroup) => button.clearWeights());
            });
            this.trackables?.forEach((trackable: CompassTrackableGroup) => {
              trackable.clearScales();
            });
            this.clearScoreGroups(false);
          } else {
            if (this.scoreClass)
              this.scoreClass.value = Compass.STANDARD_SCORE_CLASS;
            this.clearScoreGroups(false);
            this.setScoreGroups(this.savedScoreGroups);
            this.clearScaleRanges();
            this.setScaleRanges(this.savedScaleRanges);
            this.answerables?.forEach((answerable: CompassAnswerableGroup) => {
              answerable.buttons?.forEach((button: CompassAnswerableButtonGroup) => button.setWeights(button.savedWeights));
            });
            this.trackables?.forEach((trackable: CompassTrackableGroup) => {
              trackable.setScales(trackable.savedScales);
            });
          }
        }
      }
    );
    this.useCustomScoreClassLinkDependency.addDependencies(this.useCustomScoreClass);
    this.rangesStatusField.addDependencies(this.useCustomScoreClass);
    this.rangesStatusField?.update();
    this.scoreClass = new SavedTextField(from ? from.scoreClass : Compass.STANDARD_SCORE_CLASS, null, generalGroup.updators, [this.useCustomScoreClass],
      [
        (field: SavedTextField): string => this.usesCustomScoreClass() && (field.value == null || !/^(|([A-Z][a-zA-Z0-9_]*))$/g.test(field.value)) ? 'Invalid class name' : '',
        (field: SavedTextField): string => this.usesCustomScoreClass() && field.value === Compass.STANDARD_SCORE_CLASS ? 'Used by standard score class' : ''
      ], scoreClassIsEditable, null, true, null, 64
    );
    this.description = new SavedTextField(from ? from.description : null, null, generalGroup.updators, null, null, isEditable, null,
      true, null, 255);
    this.longDescription = new SavedTextField(from ? from.longDescription : null, null, generalGroup.updators, null, null, isEditable, null,
      false, null, 2000000);
    this.setTrackables(this.savedTrackables);
    this.setDocuments(this.savedDocuments);
    this.setAnswerables(this.savedAnswerables);
    if (this.usesCustomScoreClass()) {
      this.clearScoreGroups(false);
      this.clearScaleRanges();
    } else {
      this.setScoreGroups(this.savedScoreGroups);
      this.setScaleRanges(this.savedScaleRanges);
    }
    this.conclusionText = new SavedTextField(from?.assessment ? from.assessment.conclusionText : null, null, assessmentGroup.updators,
      [this.answerableStatusField, this.assessmentAccessControl], [
      (field: SavedField): string => this.answerables.length > 0 && (field.value == null || field.value.length == 0) ? 'Required' : ''
    ], (): boolean => {
      return isEditable() && (this.isOriginal() || !this.assessmentAccessControl.has(CompassAccessControl.ASSESSMENT_CONCLUSION_EDITING_ALLOWED));
    }, (oldValue: string, newValue: string): void => {
      const hasNew: boolean = newValue != null && newValue.length > 0;
      if (hasNew != (oldValue != null && oldValue.length > 0))
        this.assessmentFieldsDependency.add(hasNew ? 1 : -1);
    });
    this.attribution = new SavedTextField(from?.assessment ? from.assessment.attribution : null, null, assessmentGroup.updators,
      [this.assessmentAccessControl], null, (): boolean => {
        return isEditable() && (this.isOriginal() || !this.assessmentAccessControl.has(CompassAccessControl.ASSESSMENT_ATTRIBUTION_EDITING_ALLOWED));
      }, (oldValue: string, newValue: string): void => {
        const hasNew: boolean = newValue != null && newValue.length > 0;
        if (hasNew != (oldValue != null && oldValue.length > 0))
          this.assessmentFieldsDependency.add(hasNew ? 1 : -1);
      });
    this.attributionLink = new SavedTextField(from?.assessment ? from.assessment.attributionLink : null, null, assessmentGroup.updators,
      [this.assessmentAccessControl], null, (): boolean => {
        return isEditable() && (this.isOriginal() || !this.assessmentAccessControl.has(CompassAccessControl.ASSESSMENT_ATTRIBUTION_EDITING_ALLOWED));
      }, (oldValue: string, newValue: string): void => {
        const hasNew: boolean = newValue != null && newValue.length > 0;
        if (hasNew != (oldValue != null && oldValue.length > 0))
          this.assessmentFieldsDependency.add(hasNew ? 1 : -1);
      });
    this.showPreviousAnswers = new SavedSelectField<boolean>(from?.assessment?.showPreviousAnswers || false, null,
      assessmentGroup.updators, [this.answerableStatusField], [
      (field: SavedSelectField<boolean>): string => (this.answerables.length > 0 && field.value == null) ? 'Required' : ''
    ], false, null, null, new SelectOptions<boolean>([
      { value: true, displayValue: 'Yes' }, { value: false, displayValue: 'No' }
    ], (a: SelectOption<boolean>, b: SelectOption<boolean>): number => (b.value ? 1 : 0) - (a.value ? 1 : 0)), isEditable,
      (oldValue: boolean, newValue: boolean): void => this.assessmentFieldsDependency.add((newValue ? 1 : 0) - (oldValue ? 1 : 0))
    );
    this.hasHuddyReminder = new SavedSelectField<boolean>(from?.assessment?.frequencyReminder != null, null, assessmentGroup.updators,
      [this.assessmentAccessControl], null, false, null, null, new SelectOptions<boolean>([
        { value: true, displayValue: 'Yes' }, { value: false, displayValue: 'No' }
      ], (a: SelectOption<boolean>, b: SelectOption<boolean>): number => (b.value ? 1 : 0) - (a.value ? 1 : 0)), (): boolean => {
        return isEditable() && (this.isOriginal() || !this.assessmentAccessControl.has(CompassAccessControl.ASSESSMENT_REMINDER_EDITING_ALLOWED));
      },
      (oldValue: boolean, newValue: boolean): void => {
        this.assessmentFieldsDependency.add((newValue ? 1 : 0) - (oldValue ? 1 : 0));
        if (oldValue != newValue) {
          if (newValue) {
            this.reminderDaysBetween?.reset();
            this.reminderPrompt?.reset();
            this.reminderCaredForPrompt?.reset();
            this.reminderButtonText?.reset();
          } else {
            if (this.reminderDaysBetween)
              this.reminderDaysBetween.value = null;
            if (this.reminderPrompt)
              this.reminderPrompt.value = null;
            if (this.reminderCaredForPrompt)
              this.reminderCaredForPrompt.value = null;
            if (this.reminderButtonText)
              this.reminderButtonText.value = null;
          }
        }
      }
    );
    this.reminderDaysBetween = new SavedNumberField(from?.assessment?.frequencyReminder ? from.assessment.frequencyReminder.daysBetweenAssessments?.toString() : null, null,
      this.reminderGroup.updators, [this.hasHuddyReminder, this.assessmentAccessControl], [reminderValidator], (): boolean => {
        return isEditable() && (this.isOriginal() || !this.assessmentAccessControl.has(CompassAccessControl.ASSESSMENT_REMINDER_EDITING_ALLOWED));
      }, null, false, 1, null, true);
    this.reminderPrompt = new SavedTextField(from?.assessment?.frequencyReminder ? from.assessment.frequencyReminder.promptText : null, null,
      this.reminderGroup.updators, [this.hasHuddyReminder, this.assessmentAccessControl], [reminderValidator], (): boolean => {
        return isEditable() && (this.isOriginal() || !this.assessmentAccessControl.has(CompassAccessControl.ASSESSMENT_REMINDER_EDITING_ALLOWED));
      }, null, false, null, 128);
    this.reminderCaredForPrompt = new SavedTextField(from?.assessment?.frequencyReminder ? from.assessment.frequencyReminder.logForPromptText : null, null,
      this.reminderGroup.updators, [this.hasHuddyReminder, this.assessmentAccessControl], [reminderValidator], (): boolean => {
        return isEditable() && (this.isOriginal() || !this.assessmentAccessControl.has(CompassAccessControl.ASSESSMENT_REMINDER_EDITING_ALLOWED));
      }, null, false, null, 128);
    this.reminderButtonText = new SavedTextField(from?.assessment?.frequencyReminder ? from.assessment.frequencyReminder.buttonText : null, null,
      this.reminderGroup.updators, [this.hasHuddyReminder, this.assessmentAccessControl], [reminderValidator], (): boolean => {
        return isEditable() && (this.isOriginal() || !this.assessmentAccessControl.has(CompassAccessControl.ASSESSMENT_REMINDER_EDITING_ALLOWED));
      }, null, false, null, 24);
    if (!this.isNew())
      this.touchAll(true);
    this.fieldMap = {
      compass: {
        name: SpecialFieldName.THIS, handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MIN: FieldMapHandlerStrings.DEFAULT,
          DUP: FieldMapHandlerStrings.DEFAULT,
          FMT: FieldMapHandlerStrings.DEFAULT
        }
      },
      conditionName: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      description: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      longDescription: {
        name: '', handlers: {
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      name: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      providerName: {
        name: '', handlers: {
          PRM: 'Not found',
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      scoreName: {
        name: '', handlers: {
          FMT: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      scoreClass: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      trackables: { name: '' },
      assessment: {
        name: SpecialFieldName.THIS, handlers: {
          MIN: FieldMapHandlerStrings.DEFAULT
        }
      },
      scoreSummaryRanges: { name: 'scaleRanges' },
      scoreSummaryGroups: {
        name: 'scoreGroups', handlers: {
          FMT: FieldMapHandlerStrings.DEFAULT
        }
      },
      conclusionText: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT
        }
      },
      attribution: { name: '' },
      attributionLink: { name: '' },
      showPreviousAnswers: { name: '' },
      frequencyReminder: { name: SpecialFieldName.THIS },
      questions: {
        name: 'answerables', handlers: {
          MIN: FieldMapHandlerStrings.DEFAULT,
          DUP: FieldMapHandlerStrings.DEFAULT
        }
      },
      daysBetweenAssessments: {
        name: 'reminderDaysBetween', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MIN: FieldMapHandlerStrings.DEFAULT
        }
      },
      promptText: {
        name: 'reminderPrompt', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      logForPromptText: {
        name: 'reminderCaredForPrompt', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      buttonText: {
        name: 'reminderButtonText', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      }
    };
    if (!from)
      this.edit();
  }

  matchesNormalizedText(normalizedText: string, normalize: (text: string) => string): boolean {
    normalizedText = normalizedText || '';
    return normalizedText.length == 0 || this.isNew()
      || this.fieldMatchesNormalizedText(this.name, normalizedText, normalize)
      || this.fieldMatchesNormalizedText(this.providerName, normalizedText, normalize)
  }

  fieldMatchesNormalizedText(field: SavedField, normalizedText: string, normalize: (text: string) => string): boolean {
    return this.stringMatchesNormalizedText(field.savedValue, normalizedText, normalize)
      || this.stringMatchesNormalizedText(field.value, normalizedText, normalize);
  }

  stringMatchesNormalizedText(str: string, normalizedText: string, normalize: (text: string) => string): boolean {
    return normalize(str || '').includes(normalizedText || '');
  }

  getExpandCollapseState(): ECStructure<boolean> {
    return {
      trackables: this.trackables.map((trackable: CompassTrackableGroup): ECSNode<boolean> => {
        return {
          value: trackable.expanded,
          child: {
            scales: (trackable.scales || []).map((scale: CompassTrackableScaleGroup): ECSNode<boolean> => {
              return {
                value: scale.expanded
              };
            })
          }
        };
      }),
      answerables: this.answerables.map((answerable: CompassAnswerableGroup): ECSNode<boolean> => {
        return {
          value: answerable.expanded,
          child: {
            buttons: (answerable.buttons || []).map((button: CompassAnswerableButtonGroup): ECSNode<boolean> => {
              return {
                value: button.expanded
              };
            })
          }
        };
      }),
      scoreGroups: this.scoreGroups.map((group: CompassScoreGroupGroup): ECSNode<boolean> => {
        return {
          value: group.expanded
        };
      }),
      documents: this.documents.map((document: CompassDocumentsGroup): ECSNode<boolean> => {
        return {
          value: document.expanded
        };
      })
    };
  }

  getEditId(): number {
    return this.name.getDependencyId();
  }

  getEditableId(editable?: CompassEditable): number {
    return editable ? editable.getEditId() : null;
  }

  edit(editable: CompassEditable = this): void {
    this.editing = this.getEditableId(editable);
  }

  isEditing(editable: CompassEditable = this): boolean {
    return this.editing == this.getEditableId(editable) || (editable instanceof SavedCompass && editable.isNew());
  }

  isRejected(): boolean {
    return this.status == CompassStatus.REJECTED;
  }

  private clearTrackables(shouldUpdate: boolean = true) {
    this.trackables.forEach((trackable: CompassTrackableGroup) => trackable.destroy());
    this.trackables = [];
    if (shouldUpdate)
      this.trackableStatusField?.update();
  }

  private clearDocuments(shouldUpdate: boolean = true) {
    this.documents.forEach((document: CompassDocumentsGroup) => document.destroy());
    this.documents = [];
    if (shouldUpdate)
      this.documentsStatusField?.update();
  }

  private clearAnswerables(shouldUpdate: boolean = true) {
    this.answerables.forEach((answerable: CompassAnswerableGroup) => answerable.destroy());
    this.answerables = [];
    if (shouldUpdate)
      this.answerableStatusField?.update();
  }

  private clearScoreGroups(includeDefaultGroup: boolean = true, shouldUpdate: boolean = true) {
    this.scoreGroups.forEach((group: CompassScoreGroupGroup) => group.destroy());
    this.scoreGroups = [];
    if (includeDefaultGroup)
      this.addScoreGroup(null, false);
    if (shouldUpdate) {
      this.updateScoreGroupOptions();
      this.scoreGroupStatusField?.update();
    }
  }

  private clearScaleRanges(shouldUpdate: boolean = true) {
    this.scaleRanges.forEach((range: CompassScaleRangeGroup) => range.destroy());
    this.scaleRanges = [];
    this.rangeIdToIndexMap.clear();
    if (shouldUpdate)
      this.rangesStatusField?.update();
  }

  private setDocuments(compassDocuments?: Document[], forceNew: boolean = false) {
    if (compassDocuments?.length > 0) {
      if (forceNew) {
        this.clearDocuments(false);
        compassDocuments.forEach((document: Document) => this.addDocument(document, false, true));
        this.documentsStatusField?.update();
        return;
      }
      let i = 0;
      const minLength: number = Math.min(compassDocuments.length, this.documents.length);
      while (i < minLength) {
        this.documents[i].updateFrom(compassDocuments[i]);
        i++;
      }
      while (i < compassDocuments.length) {
        this.addDocument(compassDocuments[i], false);
        i++;
      }
      while (i < this.documents.length)
        this.removeDocument(i, false);
    } else
      this.clearDocuments(false);
    this.documentsStatusField?.update();
  }

  private setTrackables(compassTrackables?: CompassTrackable[], forceNew: boolean = false) {
    if (compassTrackables?.length > 0) {
      if (forceNew) {
        this.clearTrackables(false);
        compassTrackables.forEach((compassTrackable: CompassTrackable) => this.addTrackable(compassTrackable, false, true));
        this.trackableStatusField?.update();
        return;
      }
      let i = 0;
      const minLength: number = Math.min(compassTrackables.length, this.trackables.length);
      while (i < minLength) {
        this.trackables[i].updateFrom(compassTrackables[i]);
        i++;
      }
      while (i < compassTrackables.length) {
        this.addTrackable(compassTrackables[i], false);
        i++;
      }
      while (i < this.trackables.length)
        this.removeTrackable(i, false);
    } else
      this.clearTrackables(false);
    this.trackableStatusField?.update();
  }

  private setAnswerables(compassAnswerables: CompassAnswerable[], forceNew: boolean = false) {
    if (compassAnswerables?.length > 0) {
      if (forceNew) {
        this.clearAnswerables(false);
        compassAnswerables.forEach((answerable: CompassAnswerable) => this.addAnswerable(answerable, false, true));
        this.answerableStatusField?.update();
        return;
      }
      let i = 0;
      const minLength: number = Math.min(compassAnswerables.length, this.answerables.length);
      while (i < minLength) {
        this.answerables[i].updateFrom(compassAnswerables[i]);
        i++;
      }
      while (i < compassAnswerables.length) {
        this.addAnswerable(compassAnswerables[i], false);
        i++;
      }
      while (i < this.answerables.length)
        this.removeAnswerable(i, false);
    } else
      this.clearAnswerables(false);
    this.answerableStatusField?.update();
  }

  private setScoreGroups(scoreGroups: CompassScoreGroup[]) {
    if (scoreGroups?.length > 0) {
      let i = 0;
      const minLength: number = Math.min(scoreGroups.length, this.scoreGroups.length);
      while (i < minLength) {
        this.scoreGroups[i].updateFrom(scoreGroups[i]);
        i++;
      }
      while (i < scoreGroups.length) {
        this.addScoreGroup(scoreGroups[i], false);
        i++;
      }
      while (i < this.scoreGroups.length)
        this.removeScoreGroup(i, false);
    } else
      this.clearScoreGroups(this.useCustomScoreClass ? !this.usesCustomScoreClass() : true, false);
    this.updateScoreGroupOptions();
    this.scoreGroupStatusField?.update();
  }

  private setScaleRanges(scaleRanges: CompassScaleRange[]) {
    if (scaleRanges?.length > 0) {
      let i = 0;
      const minLength: number = Math.min(scaleRanges.length, this.scaleRanges.length);
      while (i < minLength) {
        this.scaleRanges[i].updateFrom(scaleRanges[i]);
        i++;
      }
      while (i < scaleRanges.length) {
        this.addScaleRange(scaleRanges[i]), false;
        i++;
      }
      while (i < this.scaleRanges.length)
        this.removeScaleRange(this.scaleRanges[i].id, false);
    } else
      this.clearScaleRanges(false);
    this.rangesStatusField?.update();
  }

  updateScoreGroupOptions(scoreGroups: CompassScoreGroupGroup[] = this.scoreGroups, canChangeName: boolean = true) {
    let unnamedCount: number = 0;
    if (canChangeName && scoreGroups.length == 1 && (scoreGroups[0].name.value || '').length > 1)
      scoreGroups[0].name.value = null;
    this.scoreGroupOptions.options = scoreGroups.map((group: CompassScoreGroupGroup): SelectOption<string> => {
      let name: string = group.name.value;
      if (!(name?.length > 0))
        name = 'Unnamed ' + (++unnamedCount);
      return { value: group.guid, displayValue: name };
    });
  }

  getSavedName(): string {
    return this.name.savedValue;
  }

  beforeGroupChanged(): void {
    this.handleBeforeGroupChanged();
  }

  onGroupChanged(): void {
    this.handleGroupChanged();
  }

  changeScaleTo(scale: CompassScaleRange[]) {
    if (this.usesCustomScoreClass() || scale.length == 0 || !this.canAddRemoveScaleRanges())
      return;
    this.clearScaleRanges();
    this.setScaleRanges(scale);
    this.scaleRanges.forEach((rangeGroup: CompassScaleRangeGroup) => {
      rangeGroup.isNew = true;
      rangeGroup.touchAll();
    });
    this.rangesStatusField?.update();
  }

  onUpdateFrom(from: Compass) {
    const wasDirty: boolean = this.isDirty();
    this.status = from ? from.status : CompassStatus.DRAFT;
    this.rejectReason = from ? from.rejectReason : null;
    this.publicationType = from ? from.publicationType : CompassPublicationType.PRIVATE;
    this.versionControlId = from ? from.versionControlId : null;
    this.sourceId = from ? from.sourceId : null;
    this.protectedSubscribers = from ? from.protectedSubscribers : null;
    this.accessControl.setList(from?.accessControl);
    this.assessmentAccessControl.setList(from?.assessment?.accessControl);
    this.licenseTiers = from ? from.licenseTiers : null;
    this.subscriberCount = from?.subscriberCount;
    this.version = from?.version;
    this.releaseVersion = from?.releaseVersion;
    this.name.savedValue = from ? from.name : null;
    this.providerName.savedValue = from ? from.providerName : null;
    this.scoreName.savedValue = from ? from.scoreName : null;
    this.conditionName.savedValue = from ? from.conditionName : null;
    this.useCustomScoreClass.savedValue = from ? from.scoreClass != Compass.STANDARD_SCORE_CLASS : false;
    this.scoreClass.savedValue = from ? from.scoreClass : Compass.STANDARD_SCORE_CLASS;
    this.description.savedValue = from ? from.description : null;
    this.longDescription.savedValue = from ? from.longDescription : null;
    this.savedTrackables = from?.trackables || [];
    this.trackables.forEach((trackable: CompassTrackableGroup, index: number) => trackable.updateFrom(this.savedTrackables[index]));
    this.trackableStatusField?.update();
    this.documents.forEach((document: CompassDocumentsGroup, index: number) => document.updateFrom(this.savedDocuments[index]));
    this.documentsStatusField?.update();
    this.savedAnswerables = from?.assessment?.questions || [];
    this.answerables.forEach((answerable: CompassAnswerableGroup, index: number) => answerable.updateFrom(this.savedAnswerables[index]));
    this.answerableStatusField?.update();
    this.savedScoreGroups = this.getSavedScoreGroups(from);
    this.scoreGroups.forEach((scoreGroups: CompassScoreGroupGroup, index: number) => scoreGroups.updateFrom(this.savedScoreGroups[index]));
    this.scoreGroupStatusField?.update();
    this.savedScaleRanges = from?.scoreSummaryRanges || [];
    this.scaleRanges.forEach((scaleRange: CompassScaleRangeGroup, index: number) => scaleRange.updateFrom(this.savedScaleRanges[index]));
    this.rangesStatusField?.update();
    this.conclusionText.savedValue = from?.assessment ? from.assessment.conclusionText : null;
    this.attribution.savedValue = from?.assessment ? from.assessment.attribution : null;
    this.attributionLink.savedValue = from?.assessment ? from.assessment.attributionLink : null;
    this.showPreviousAnswers.savedValue = from?.assessment?.showPreviousAnswers || false;
    this.hasHuddyReminder.savedValue = from?.assessment?.frequencyReminder != null;
    this.reminderDaysBetween.savedValue = from?.assessment?.frequencyReminder ? from.assessment.frequencyReminder.daysBetweenAssessments?.toString() : null;
    this.reminderPrompt.savedValue = from?.assessment?.frequencyReminder ? from.assessment.frequencyReminder.promptText : null;
    this.reminderCaredForPrompt.savedValue = from?.assessment?.frequencyReminder ? from.assessment.frequencyReminder.logForPromptText : null;
    this.reminderButtonText.savedValue = from?.assessment?.frequencyReminder ? from.assessment.frequencyReminder.buttonText : null;
    if (!wasDirty || !this.isDraft())
      this.reset(false);
    if (!this.isNew())
      this.touchAll(true);
    else
      this.edit();
  }

  touchAll(value: boolean): void {
    this.trackableStatusField.touch(value);
    this.documentsStatusField.touch(value);
    this.answerableStatusField.touch(value);
    this.scoreGroupStatusField.touch(value);
    this.rangesStatusField.touch(value);
    this.name.touch(value);
    this.providerName.touch(value);
    this.scoreName.touch(value);
    this.conditionName.touch(value);
    this.useCustomScoreClass.touch(value);
    this.scoreClass.touch(value);
    this.description.touch(value);
    this.longDescription.touch(value);
    this.trackables.forEach((trackable: CompassTrackableGroup) => trackable.touchAll(value));
    this.answerables.forEach((answerable: CompassAnswerableGroup) => answerable.touchAll(value));
    this.scoreGroups.forEach((scoreGroup: CompassScoreGroupGroup) => scoreGroup.touchAll(value));
    this.scaleRanges.forEach((rangeGroup: CompassScaleRangeGroup) => rangeGroup.touchAll(value));
    this.documents.forEach((document: CompassDocumentsGroup) => document.touchAll(value));
    this.conclusionText.touch(value);
    this.attribution.touch(value);
    this.attributionLink.touch(value);
    this.showPreviousAnswers.touch(value);
    this.hasHuddyReminder.touch(value);
    this.reminderDaysBetween.touch(value);
    this.reminderPrompt.touch(value);
    this.reminderCaredForPrompt.touch(value);
    this.reminderButtonText.touch(value);
  }

  goNext(): void {
    if (this.maxGroup == null || this.groupIndex >= this.groups.length - 1)
      return;
    this.groupIndex++;
    this.maxGroup = Math.max(this.groupIndex, this.maxGroup);
  }

  setGroupToError(): void {
    if (!this.groups[this.groupIndex].hasVisibleIssue()) {
      const index: number = this.groups.findIndex((group: SavedGroupStatus): boolean => group.hasVisibleIssue());
      if (index >= 0)
        this.groupIndex = index;
    }
    if (this.maxGroup != null)
      this.maxGroup = Math.max(this.groupIndex, this.maxGroup);
  }

  warnValidation(): void {
    this.snackbar.open('Fix validation issues', 'Dismiss', {
      duration: 3000
    });
    this.setGroupToError();
  }

  onReset(): void {
    this.name.reset();
    this.providerName.reset();
    this.scoreName.reset();
    this.conditionName.reset();
    this.useCustomScoreClass.reset();
    this.scoreClass.reset();
    this.description.reset();
    this.longDescription.reset();
    this.setScoreGroups(this.savedScoreGroups);
    this.scoreGroups.forEach((scoreGroup: CompassScoreGroupGroup) => scoreGroup.resetAll());
    this.scoreGroupStatusField.reset();
    this.scoreGroupStatusField?.update();
    this.setTrackables(this.savedTrackables);
    this.trackables.forEach((trackable: CompassTrackableGroup) => trackable.resetAll());
    this.trackableStatusField.reset();
    this.trackableStatusField?.update();
    this.setDocuments(this.savedDocuments);
    this.documents.forEach((document: CompassDocumentsGroup) => document.resetAll());
    this.documentsStatusField.reset();
    this.documentsStatusField?.update();
    this.setAnswerables(this.savedAnswerables);
    this.answerables.forEach((answerable: CompassAnswerableGroup) => answerable.resetAll());
    this.answerableStatusField.reset();
    this.answerableStatusField?.update();
    this.setScaleRanges(this.savedScaleRanges);
    this.scaleRanges.forEach((rangeGroup: CompassScaleRangeGroup) => rangeGroup.resetAll());
    this.rangesStatusField.reset();
    this.rangesStatusField?.update();
    this.conclusionText.reset();
    this.attribution.reset();
    this.attributionLink.reset();
    this.showPreviousAnswers.reset();
    this.hasHuddyReminder.reset();
    this.reminderDaysBetween.reset();
    this.reminderPrompt.reset();
    this.reminderCaredForPrompt.reset();
    this.reminderButtonText.reset();
  }

  getSaved(): Compass {
    return new Compass({
      publicationType: this.publicationType,
      versionControlId: this.versionControlId,
      sourceId: this.sourceId,
      protectedSubscribers: this.protectedSubscribers,
      accessControl: this.accessControl.getList(),
      licenseTiers: this.licenseTiers,
      status: this.isRejected() ? CompassStatus.DRAFT : this.status,
      id: this.id,
      version: this.version,
      name: this.name.value,
      providerName: this.providerName.value,
      scoreName: this.scoreName.value,
      scoreClass: this.scoreClass.value,
      conditionName: this.conditionName.value,
      description: this.description.value,
      longDescription: this.longDescription.value,
      trackables: this.trackables.map((trackable: CompassTrackableGroup): CompassTrackable => trackable.getSaved()),
      documents: this.documents.map((document: CompassDocumentsGroup): Document => document.getSaved()),
      scoreSummaryGroups: this.scoreGroups.map((scoreGroup: CompassScoreGroupGroup): CompassScoreGroup => scoreGroup.getSaved()),
      scoreSummaryRanges: this.scaleRanges.map((rangeGroup: CompassScaleRangeGroup): CompassScaleRange => rangeGroup.getSaved()),
      assessment: this.answerables.length == 0 ? null : new CompassAssessment({
        accessControl: this.assessmentAccessControl.getList(),
        conclusionText: this.conclusionText.value,
        attribution: this.attribution.value,
        attributionLink: this.attributionLink.value,
        showPreviousAnswers: this.showPreviousAnswers.value,
        frequencyReminder: this.hasHuddyReminder.value ? new CompassAnswerableReminder({
          daysBetweenAssessments: (this.reminderDaysBetween.value != null && this.reminderDaysBetween.value.length > 0) ? parseInt(this.reminderDaysBetween.value) : null,
          promptText: this.reminderPrompt.value,
          logForPromptText: this.reminderCaredForPrompt.value,
          buttonText: this.reminderButtonText.value
        }) : null,
        questions: this.answerables.map((answerable: CompassAnswerableGroup): CompassAnswerable => answerable.getSaved())
      })
    });
  }

  compassSaveEnabled(): boolean {
    return this.saveEnabled() && this.name?.value?.length > 0 && this.providerName?.value?.length > 0;
  }

  async onSave(displayErrorIfPresent: boolean = true): Promise<boolean> {
    this.edit(null);
    this.maxGroup = null;
    if (!this.compassSaveEnabled())
      return Promise.resolve(false);
    return this.store.dispatch(new SaveCompass(this.getSaved())).toPromise().then(() => true).catch((error: any) => {
      let doDefault: boolean = false, handled: boolean = true;
      if (error instanceof HttpErrorResponse && error.error instanceof Array) {
        error.error.filter((e: any) => typeof e.code == 'string' && typeof e.field == 'string' && typeof e.message == 'string' && typeof e.objectName == 'string').forEach((e: any) => {
          let field: FieldMap | SavedField = this, handler: FieldMapHandler = null;
          (e.field as string).split('.').forEach((prop: string) => {
            if (field == null)
              return;
            const bracketIndex: number = prop.indexOf('[');
            let propName: string, propIndex: number;
            if (bracketIndex > -1) {
              propName = prop.substring(0, bracketIndex);
              propIndex = parseInt(prop.substring(bracketIndex + 1, prop.length - 1));
            } else {
              propName = prop;
              propIndex = null;
            }
            const entry: FieldMapEntry = (field as FieldMap).fieldMap[propName];
            if (!entry) {
              field = null;
              handler = null;
              return;
            }
            handler = entry.handlers ? entry.handlers[e.code] : null;
            let fieldName: string = entry.name;
            if (fieldName == null)
              field = null;
            else if (fieldName == SpecialFieldName.THIS) {
              if (propIndex != null)
                field = field[propIndex];
            } else {
              if (fieldName.length == 0)
                fieldName = propName;
              field = propIndex != null ? field[fieldName][propIndex] : field[fieldName];
            }
          });
          if (!handler) {
            if (handled)
              handled = false;
            return;
          }
          if (typeof handler == 'string') {
            let message: string = handler;
            switch (handler) {
              case FieldMapHandlerStrings.DEFAULT:
                if (!doDefault)
                  doDefault = true;
                break;
              case FieldMapHandlerStrings.MESSAGE:
                message = e.message;
              default:
                if (!(field instanceof SavedField)) {
                  if (handled)
                    handled = false;
                  return;
                }
                field.error = message;
            }
          } else
            handler(field, e);
        });
      } else
        handled = false;
      this.setGroupToError();
      if (doDefault)
        this.touchAll(true);
      if (handled && this.error > 0) {
        this.errorHandler.handleSimple(error, DesktopErrorHandler.MESSAGE_SILENT);
        if (displayErrorIfPresent) {
          this.snackbar.open('Fix validation issues', 'Dismiss', {
            duration: 3000
          });
        }
      } else if (displayErrorIfPresent)
        this.errorHandler.handle(error, 'compass');
      return false;
    });
  }

  async onDeleteExisting(displayErrorIfPresent: boolean = true): Promise<boolean> {
    return this.store.dispatch(new DeleteCompass(this.id)).toPromise().then(() => true).catch((error: any) => {
      if (displayErrorIfPresent)
        this.errorHandler.handle(error, this.name.savedValue);
      return false;
    });
  }

  onDeleteNew(): void {
    this.removeSavedCompasses([this]);
  }

  onDetach(): void {
    this.providerOptionsSubscription?.unsubscribe();
  }

  getProvider(): Provider {
    if (this.providerName.savedValue == null)
      return null;
    return this.providerMap.get(this.providerName.savedValue)
  }

  getSavedScoreGroups(from?: Compass): CompassScoreGroup[] {
    return from?.scoreSummaryGroups || ((from && from.scoreClass != Compass.STANDARD_SCORE_CLASS) ? [] : [new CompassScoreGroup({ guid: CompassScoreGroupGroup.generateGUID() })]);
  }

  isSingleGroup(): boolean {
    return this.scoreGroups.length == 1;
  }

  isOriginal(): boolean {
    return !this.sourceId;
  }

  canAddTrackables(): boolean {
    return this.isOriginal() || !this.accessControl.has(CompassAccessControl.COMPASS_CAN_ADD_TRACKABLES);
  }

  canAddRemoveScaleRanges(): boolean {
    return this.isOriginal() || !(this.accessControl.has(CompassAccessControl.SCORE_SUMMARY_TEXT_EDITING_ALLOWED) || this.accessControl.has(CompassAccessControl.SCORE_SUMMARY_SCALE_EDITING_ALLOWED));
  }

  addTrackable(from?: CompassTrackable, shouldUpdate: boolean = true, forceNew: boolean = false) {
    const trackable: CompassTrackableGroup = new CompassTrackableGroup(from, this.groups[1].updators, this.nameDependency, this, forceNew);
    this.trackables.push(trackable);
    if (shouldUpdate)
      this.trackableStatusField?.update();
    if (!from)
      this.edit(trackable);
  }


  addDocument(from?: Document, shouldUpdate: boolean = true, forceNew: boolean = false) {
    const document: CompassDocumentsGroup = new CompassDocumentsGroup(from, this.groups[5].updators, this, forceNew);
    this.documents.push(document);
    if (shouldUpdate)
      this.documentsStatusField?.update();
    if (!from)
      this.edit(document);
  }

  addAnswerable(from?: CompassAnswerable, shouldUpdate: boolean = true, forceNew: boolean = false, adaptGroupGUIDs: boolean = false) {
    this.answerables.push(new CompassAnswerableGroup(from, this.answerablesGroup.updators, this, forceNew, adaptGroupGUIDs));
    if (shouldUpdate)
      this.answerableStatusField?.update();
  }

  removeTrackable(index: number, shouldUpdate: boolean = true) {
    this.trackables[index].destroy();
    this.trackables.splice(index, 1);
    if (shouldUpdate)
      this.trackableStatusField?.update();
  }

  removeDocument(index: number, shouldUpdate: boolean = true) {
    this.documents[index].destroy();
    this.documents.splice(index, 1);
    if (shouldUpdate)
      this.documentsStatusField?.update();
  }

  removeAnswerable(index: number, shouldUpdate: boolean = true) {
    this.answerables[index].destroy();
    this.answerables.splice(index, 1);
    if (shouldUpdate)
      this.answerableStatusField?.update();
  }

  moveAnswerable(fromIndex: number, toIndex: number): boolean {
    if (fromIndex == toIndex)
      return false;
    const fromAnswerable: CompassAnswerableGroup = this.answerables[fromIndex], toAnswerable: CompassAnswerableGroup = this.answerables[toIndex];
    if (!fromAnswerable || !toAnswerable)
      return false;
    const tempAnswerable: CompassAnswerable = fromAnswerable.getSaved(), tempExpanded: boolean = fromAnswerable.expanded;
    fromAnswerable.changeTo(toAnswerable.getSaved(), toAnswerable.expanded);
    toAnswerable.changeTo(tempAnswerable, tempExpanded);
    return true;
  }

  addScoreGroup(from?: CompassScoreGroup, updateOptionsAndStatus: boolean = true) {
    this.scoreGroups.push(new CompassScoreGroupGroup(from, this.scoreGroupsGroup.updators, this));
    if (updateOptionsAndStatus) {
      this.updateScoreGroupOptions();
      this.scoreGroupStatusField?.update();
    }
  }

  removeScoreGroup(index: number, updateOptionsAndStatus: boolean = true) {
    if (index == null || this.scoreGroups.length == 0)
      return;
    this.scoreGroups[index].destroy();
    this.scoreGroups.splice(index, 1);
    if (updateOptionsAndStatus) {
      this.updateScoreGroupOptions();
      this.scoreGroupStatusField?.update();
    }
  }

  addScaleRange(from?: CompassScaleRange, shouldUpdate: boolean = true) {
    const range: CompassScaleRangeGroup = new CompassScaleRangeGroup(from, this.scaleRangesGroup.updators, this.rangesDependency, this.rangeIdToIndexMap, this.maxScoreDependency, this);
    this.rangeIdToIndexMap.set(range.id, this.scaleRanges.length);
    this.scaleRanges.push(range);
    if (shouldUpdate)
      this.rangesStatusField?.update();
  }

  removeScaleRange(id: number, shouldUpdate: boolean = true) {
    const index: number = this.rangeIdToIndexMap.get(id);
    if (index == null)
      return;
    this.scaleRanges[index].destroy();
    this.scaleRanges.splice(index, 1);
    for (let i = index; i < this.scaleRanges.length; i++)
      this.rangeIdToIndexMap.set(this.scaleRanges[i].id, i);
    this.rangeIdToIndexMap.delete(id);
    if (shouldUpdate)
      this.rangesStatusField?.update();
  }

  getScoreGroup(guid: string): CompassScoreGroupGroup {
    return this.scoreGroups.find((group: CompassScoreGroupGroup): boolean => group.guid == guid);
  }

  usesCustomScoreClass(): boolean {
    return this.useCustomScoreClass?.value === true;
  }

  hasReminder(): boolean {
    return this.hasHuddyReminder.value === true;
  }

  isDraft(): boolean {
    return this.status == CompassStatus.DRAFT;
  }

  isPrivate(): boolean {
    return this.publicationType == CompassPublicationType.PRIVATE;
  }

  isProtected(): boolean {
    return this.publicationType == CompassPublicationType.PROTECTED;
  }

  canPublish(): boolean {
    return this.isDraft() && !(this.isDirty() || this.isInvalid());
  }

  publish(): void {
    this.dialog.open(PublishCompassDialog, {
      width: '636px',
      data: {
        compass: this.getSaved()
      }
    });
  }

  updateSettings(): void {
    this.dialog.open(PublishCompassDialog, {
      width: '636px',
      data: {
        compass: this.getSaved()
      }
    });
  }

  getStatusText(): string {
    switch (this.status) {
      case CompassStatus.IN_REVIEW:
        return 'In Review';
      case CompassStatus.REJECTED:
        return 'Rejected';
      case CompassStatus.PUBLISHED:
        switch (this.publicationType) {
          case CompassPublicationType.PRIVATE:
            return 'Private';
          case CompassPublicationType.PROTECTED:
            return 'Protected';
          case CompassPublicationType.PUBLIC:
            return 'Public';
          default:
            return null;
        }
      case CompassStatus.ARCHIVED:
        return 'Archived';
      case CompassStatus.DRAFT:
        return 'Draft';
      default:
        return null;
    }
  }

  static compare(a: SavedCompass, b: SavedCompass): number {
    let comp: number = a.getListingName().toLocaleLowerCase().localeCompare(b.getListingName().toLocaleLowerCase());
    if (comp != 0)
      return comp;
    const ap: Provider = a.getProvider(), bp: Provider = b.getProvider();
    const apName: string = ap?.name || null, bpName: string = bp?.name || null;
    if (apName || bpName) {
      if (!bpName)
        return 1;
      if (!apName)
        return -1;
      comp = apName.toLocaleUpperCase().localeCompare(bpName.toLocaleLowerCase());
      if (comp != 0)
        return comp;
    }
    comp = (bp?.id || 0) - (ap?.id || 0);
    if (comp != 0)
      return comp;
    return (b.id || 0) - (a.id || 0);
  }
}

interface ConditionSR {
  id: number;
  name: string;
}

export class CompassTrackableGroup extends SavedGroupStatus implements CompassEditable, FieldMap, Collapsible {
  trackableId: number;
  sourceCompassTrackableId: number;
  accessControl: SavedAccessControl;
  name: SavedTextField;
  isIntegrated: SimpleSavedField<boolean>;
  possibleIntegrations: SelectOptions<CompassTrackableIntegrationType>;
  integrations: SavedMultiselectField<CompassTrackableIntegrationType>;
  trackableType: SavedSelectField<string>;
  unitsOfMeasure: SavedTextField;
  normalValue: SavedNumberField;
  additive: SavedSelectField<boolean>;
  scalesGroup: SavedGroupStatus;
  scales: CompassTrackableScaleGroup[];
  savedScales: TrackableValuation[];
  nameDependency: MapCountDependency<string>;
  scoreGroupMemberDependency: MapCountDependency<string>;
  rangesDependency: RangeOverlapDependency<Range>;
  statusField: SimpleStatusField;
  isNew: boolean;
  scoreGroupOptionsSubscription: Subscription;
  compass: SavedCompass;
  fieldMap: FieldMapType;
  expanded: boolean;

  constructor(from: CompassTrackable, parentUpdators: CountUpdators, nameDependency: MapCountDependency<string>,
    compass: SavedCompass, forceNew: boolean = false) {
    super(parentUpdators);
    this.expanded = false;
    this.isNew = forceNew || from == null;
    this.compass = compass;
    this.possibleIntegrations = new SelectOptions<CompassTrackableIntegrationType>();
    this.trackableId = from ? from.trackableId : null;
    this.sourceCompassTrackableId = from ? from.sourceCompassTrackableId : null;
    this.accessControl = new SavedAccessControl(from?.accessControl);
    this.compass?.trackableStatusField.addDependencies(this.accessControl);
    this.nameDependency = nameDependency;
    this.scoreGroupMemberDependency = new MapCountDependency<string>();
    this.rangesDependency = new RangeOverlapDependency();
    this.scalesGroup = new SavedGroupStatus(this.updators);
    this.scales = [];
    this.savedScales = this.getScales(from);
    this.scoreGroupOptionsSubscription = this.compass.scoreGroupOptions.optionsChanged.subscribe((change: SelectOptionsChange<string>) => {
      if (change.options.length == 1 && this.scales.length > 0) {
        const guid: string = change.options[0].value;
        let keepIndex: number = this.scales.findIndex((scale: CompassTrackableScaleGroup): boolean => (scale.groupGuids.value || []).findIndex((g: string): boolean => g == guid) >= 0);
        if (keepIndex < 0)
          keepIndex = 0;
        const length: number = this.scales.length;
        for (let i = 0; i < length; i++) {
          if (i != keepIndex)
            this.removeScale(i < keepIndex ? 0 : 1, false);
        }
        this.statusField?.update();
      }
    });
    const evidenceValidator: (field: SavedField) => string = (field: SavedField): string =>
      (this.isEvidence() && (field.value == null || field.value.length == 0)) ? 'Required' : null;
    this.statusField = new SimpleStatusField(this.scalesGroup.updators, null, null, (): boolean =>
      this.scales.length != this.savedScales.length || this.scales.findIndex((scale: CompassTrackableScaleGroup): boolean => scale.isNew) != -1);
    const isEditable: () => boolean = (): boolean => !this.compass || this.compass.isEditing() || this.compass.isEditing(this);
    this.isIntegrated = new SimpleSavedField<boolean>(this.getIsIntegrated(from), null, this.updators, null, null, null, null, null, (oldValue: boolean, newValue: boolean): void => {
      if (oldValue && !newValue)
        this.integrations.value = [];
    });
    this.name = new SavedTextField(from ? from.name : null, null, this.updators, [nameDependency, this.accessControl],
      [(field: SavedTextField): string => nameDependency.count(field.value) > 1 ? 'Duplicate' : null], (): boolean => {
        return isEditable() && !this.isIntegratedTrackable() && (!this.compass || this.compass.isOriginal() || !this.accessControl.has(CompassAccessControl.TRACKABLE_NAME_EDITING_ALLOWED));
      }, (oldValue: string, newValue: string): void => {
        this.possibleIntegrations.options = (this.compass.possibleIntegrationsMap.get(newValue) || []).map((integration: CompassTrackableIntegrationType): SelectOption<CompassTrackableIntegrationType> => {
          return { value: integration, displayValue: CompassTrackableIntegrationTypeUtil.getIntegrationDisplayValue(integration) };
        });
        if (oldValue != null && newValue != null)
          nameDependency.replace(oldValue, newValue);
        else if (oldValue != null)
          nameDependency.subtract(oldValue);
        else if (newValue != null)
          nameDependency.add(newValue);
        this.compass.setAutocompleteTrackableFilterText(newValue);
      }, true, null, 50);
    this.integrations = new SavedMultiselectField<CompassTrackableIntegrationType>(this.getIntegrations(from), null, this.updators,
      [this.isIntegrated], [(field: SavedMultiselectField<CompassTrackableIntegrationType>): string => this.isIntegratedTrackable() && !(field.value?.length > 0) ? 'Required' : null],
      false, null, null, this.possibleIntegrations, isEditable, null);
    this.trackableType = new SavedSelectField<string>(from ? from.trackableType : null, null, this.updators, null, null,
      true, null, null, new SelectOptions<string>([
        { value: 'SYMPTOM', displayValue: 'Symptom' }, { value: 'EVIDENCE', displayValue: 'Evidence' },
        { value: 'TREATMENT', displayValue: 'Treatment' }, { value: 'ACTION', displayValue: 'Action' }
      ]), (): boolean => isEditable() && !this.isIntegratedTrackable(), (oldValue: string, newValue: string): void => {
        if (newValue != oldValue) {
          if (newValue === 'EVIDENCE') {
            this.unitsOfMeasure?.reset();
            this.normalValue?.reset();
            this.additive?.reset();
          } else if (oldValue == 'EVIDENCE') {
            if (this.unitsOfMeasure)
              this.unitsOfMeasure.value = null;
            if (this.normalValue)
              this.normalValue.value = null;
            if (this.additive)
              this.additive.value = false;
          }
        }
      }
    );
    this.unitsOfMeasure = new SavedTextField(from ? from.unitsOfMeasure : null, null, this.updators, [this.trackableType], [evidenceValidator], (): boolean => isEditable() && !this.isIntegratedTrackable(), null,
      false, null, 255);
    this.normalValue = new SavedNumberField(from ? from.normalValue?.toString() : null, null, this.updators, [this.trackableType],
      [evidenceValidator], isEditable, null, false, null, null, false);
    this.additive = new SavedSelectField<boolean>(from?.additive || false, null, this.updators, [this.trackableType],
      [evidenceValidator], false, null, null, new SelectOptions<boolean>([
        { value: true, displayValue: 'Yes' }, { value: false, displayValue: 'No' }
      ], (a: SelectOption<boolean>, b: SelectOption<boolean>): number => (b.value ? 1 : 0) - (a.value ? 1 : 0)), (): boolean => isEditable() && !this.isIntegratedTrackable()
    );
    this.compass.usesCustomScoreClass() ? this.clearScales() : this.setScales(this.savedScales);
    if (!this.isNew)
      this.touchAll(true);
    this.fieldMap = {
      name: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      trackableType: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT
        }
      },
      unitsOfMeasure: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      normalValue: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT
        }
      },
      additive: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT
        }
      },
      trackableValuations: {
        name: 'scales', handlers: {
          DUP: FieldMapHandlerStrings.DEFAULT,
          FMT: FieldMapHandlerStrings.DEFAULT
        }
      },
      trackableName: {
        name: 'name', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      }
    };
  }

  getIsIntegrated(from?: CompassTrackable): boolean {
    return from ? from.isIntegrated == true : false;
  }

  getIntegrations(from?: CompassTrackable): CompassTrackableIntegrationType[] {
    return from?.integrations || [];
  }

  isIntegratedTrackable(): boolean {
    return this.isIntegrated?.value == true;
  }

  setIntegration(integrationTrackable: IntegrationTrackable): void {
    this.isIntegrated.value = true;
    this.integrations.value = integrationTrackable.integrations;
    this.name.value = integrationTrackable.name;
    this.trackableType.value = integrationTrackable.type;
    if (integrationTrackable.type == 'EVIDENCE') {
      this.unitsOfMeasure.value = integrationTrackable.units;
      this.additive.value = integrationTrackable.isAdditive;
    }
  }

  clearIntegration(): void {
    this.isIntegrated.value = false;
  }

  getEditId(): number {
    return this.name.getDependencyId();
  }

  clearScales(shouldUpdate: boolean = true) {
    this.scales.forEach((scale: CompassTrackableScaleGroup) => scale.destroy());
    this.scales = [];
    if (shouldUpdate)
      this.statusField?.update();
  }

  setScales(scales: TrackableValuation[]) {
    if (scales?.length > 0) {
      let i = 0;
      const minLength: number = Math.min(scales.length, this.scales.length);
      while (i < minLength) {
        this.scales[i].updateFrom(scales[i]);
        i++;
      }
      while (i < scales.length) {
        this.addScale(scales[i]), false;
        i++;
      }
      while (i < this.scales.length)
        this.removeScale(i, false);
    } else
      this.clearScales(false);
    this.statusField?.update();
  }

  destroy() {
    if (this.compass?.isEditing(this))
      this.compass.edit(null);
    if (this.name.value != null)
      this.nameDependency.subtract(this.name.value);
    this.scales.forEach((scale: CompassTrackableScaleGroup) => scale.destroy());
    this.compass?.trackableStatusField.removeDependency(this.accessControl);
    if (this.scoreGroupOptionsSubscription)
      this.scoreGroupOptionsSubscription.unsubscribe();
    this.detach();
  }

  changeTo(trackable: CompassTrackable) {
    trackable = trackable.clone();
    if (this.compass.usesCustomScoreClass())
      trackable.trackableValuations = [];
    if (!this.canEditScale()) {
      trackable.trackableValuations = this.getSaved().trackableValuations;
      const trackableName: string = this.name.isEditable() ? trackable.name : this.name.savedValue;
      (trackable.trackableValuations || []).forEach((valuation: TrackableValuation) => valuation.trackableName = trackableName);
    }
    if (this.compass.scoreGroups.length == 1) {
      if (trackable.trackableValuations?.length > 0) {
        const guid: string = this.compass.scoreGroups[0].guid;
        let keepIndex: number = trackable.trackableValuations.findIndex((valuation: TrackableValuation): boolean => valuation.groupGuids.findIndex((g: string): boolean => g == guid) >= 0);
        if (keepIndex == null || keepIndex < 0)
          keepIndex = 0;
        trackable.trackableValuations = [new TrackableValuation({
          ...trackable.trackableValuations[keepIndex],
          groupGuids: [guid]
        })];
      }
    } else {
      (trackable.trackableValuations || []).forEach((valuation: TrackableValuation) => (valuation.groupGuids || []).filter(
        (guid: string): boolean => this.compass.getScoreGroup(guid) != null
      ));
    }
    this.updateFrom(trackable, true);
    this.resetAll();
    this.compass.trackableStatusField?.update();
  }

  updateFrom(from: CompassTrackable, forceNew?: boolean) {
    this.isNew = forceNew != null ? forceNew : from == null;
    this.isIntegrated.savedValue = this.getIsIntegrated(from);
    this.trackableId = from ? from.trackableId : null;
    this.sourceCompassTrackableId = from ? from.sourceCompassTrackableId : null;
    this.accessControl.setList(from?.accessControl);
    this.name.savedValue = from ? from.name : null;
    this.integrations.savedValue = this.getIntegrations(from),
      this.trackableType.savedValue = from ? from.trackableType : null;
    this.unitsOfMeasure.savedValue = from ? from.unitsOfMeasure : null;
    this.normalValue.savedValue = from ? from.normalValue?.toString() : null;
    this.additive.savedValue = from?.additive || false;
    this.savedScales = this.getScales(from);
    this.scales.forEach((scale: CompassTrackableScaleGroup, index: number) => scale.updateFrom(this.savedScales[index]));
    this.statusField?.update();
    if (!this.isNew)
      this.touchAll(true);
  }

  touchAll(value: boolean = true) {
    this.statusField.touch(value);
    this.name.touch(value);
    this.isIntegrated.touch(value);
    this.integrations.touch(value);
    this.trackableType.touch(value);
    this.unitsOfMeasure.touch(value);
    this.normalValue.touch(value);
    this.additive.touch(value);
    this.scales.forEach((scale: CompassTrackableScaleGroup) => scale.touchAll(value));
  }

  resetAll() {
    this.name.reset();
    this.isIntegrated.reset();
    this.integrations.reset();
    this.trackableType.reset();
    this.unitsOfMeasure.reset();
    this.normalValue.reset();
    this.additive.reset();
    this.setScales(this.savedScales);
    this.scales.forEach((scale: CompassTrackableScaleGroup) => scale.resetAll());
    this.statusField.reset();
    this.statusField?.update();
  }

  getSaved(): CompassTrackable {
    return new CompassTrackable({
      trackableId: this.trackableId,
      sourceCompassTrackableId: this.sourceCompassTrackableId,
      accessControl: this.accessControl.getList(),
      name: this.name.value,
      isIntegrated: this.isIntegrated.value,
      integrations: this.integrations.value,
      trackableType: this.trackableType.value,
      unitsOfMeasure: this.unitsOfMeasure.value,
      normalValue: (this.normalValue.value != null && this.normalValue.value.length > 0) ? parseFloat(this.normalValue.value) : null,
      additive: this.isEvidence() ? this.additive.value : null,
      trackableValuations: this.scales.map((scale: CompassTrackableScaleGroup): TrackableValuation => scale.getSaved(this.name.value))
    });
  }

  getScales(from?: CompassTrackable): TrackableValuation[] {
    return from?.trackableValuations || [];
  }

  addScale(from?: TrackableValuation, shouldUpdate: boolean = true) {
    this.scales.push(new CompassTrackableScaleGroup(from, this.scalesGroup.updators, this));
    if (shouldUpdate)
      this.statusField?.update();
  }

  removeScale(index: number, shouldUpdate: boolean = true) {
    if (index == null)
      return;
    this.scales[index].destroy();
    this.scales.splice(index, 1);
    if (shouldUpdate)
      this.statusField?.update();
  }

  isEvidence(): boolean {
    return this.trackableType.value === 'EVIDENCE';
  }

  canRemove(): boolean {
    return !this.compass || this.compass.isOriginal() || !this.accessControl.has(CompassAccessControl.TRACKABLE_DELETION_ALLOWED);
  }

  canEditScale(): boolean {
    return !this.compass || this.compass.isOriginal() || !this.accessControl.has(CompassAccessControl.TRACKABLE_SCALE_EDITING_ALLOWED);
  }
}

export class CompassTrackableScaleGroup extends SavedGroupStatus implements FieldMap, Collapsible {
  trackableName: string;
  maxScoreDependencies: Map<string, SumContributionGroupsDependency>;
  groupGuids: SavedMultiselectField<string>;
  scaleType: SavedSelectField<CompassTrackableScaleType>;
  scaleRangesGroup: SavedGroupStatus;
  scaleRanges: TrackableScaleRangeGroup[];
  savedScaleRanges: TrackableScaleRange[];
  rangesDependency: RangeOverlapDependency<Range>;
  rangeIdToIndexMap: Map<number, number>;
  statusField: SimpleStatusField;
  isNew: boolean;
  accessControl: SavedAccessControl;
  trackable: CompassTrackableGroup;
  scoreGroupOptionsSubscription: Subscription;
  compass: SavedCompass;
  fieldMap: FieldMapType;
  expanded: boolean;

  constructor(from: TrackableValuation, parentUpdators: CountUpdators, trackable: CompassTrackableGroup, forceNew: boolean = false) {
    super(parentUpdators);
    this.expanded = false;
    this.isNew = forceNew || from == null;
    this.trackable = trackable;
    this.compass = this.trackable?.compass;
    this.accessControl = trackable?.accessControl;
    this.rangesDependency = new RangeOverlapDependency();
    this.rangeIdToIndexMap = new Map<number, number>();
    this.scaleRangesGroup = new SavedGroupStatus(this.updators);
    this.scaleRanges = [];
    this.savedScaleRanges = this.getScaleRanges(from);
    this.statusField = new SimpleStatusField(this.scaleRangesGroup.updators, null,
      [() => !this.scaleType || this.scaleType.value == null || this.scaleRanges?.length > 0 ? '' : 'Must provide at least one scale range'], (): boolean =>
      this.scaleRanges.length != this.savedScaleRanges.length || this.scaleRanges.findIndex((scaleRange: TrackableScaleRangeGroup): boolean => scaleRange.isNew) != -1);
    this.trackableName = from?.trackableName;
    let groupGuidDependencies: Dependency[] = [this.accessControl];
    if (this.trackable)
      groupGuidDependencies.push(this.trackable.scoreGroupMemberDependency);
    const isEditable: () => boolean = (): boolean => !this.compass || this.compass.isEditing() || this.compass.isEditing(this.trackable);
    this.groupGuids = new SavedMultiselectField<string>(from?.groupGuids || [], null, this.updators, groupGuidDependencies, [
      (field: SavedMultiselectField<string>): string => {
        return (this.trackable && (field.value || []).findIndex((guid: string): boolean => this.trackable.scoreGroupMemberDependency.count(guid) > 1) >= 0) ? 'Duplicate' : ''
      }
    ], true, null, null, this.compass?.scoreGroupOptions || new SelectOptions<string>(), (): boolean => {
      return isEditable() && (!this.compass || this.compass.isOriginal() || !this.accessControl.has(CompassAccessControl.TRACKABLE_SCALE_EDITING_ALLOWED));
    }, (_: string[], newValue: string[], __: SavedMultiselectField<string>, selected: string[], unselected: string[]): void => {
      if (!this.compass)
        return;
      this.maxScoreDependencies = new Map<string, SumContributionGroupsDependency>(
        (newValue || [])
          .filter((guid: string): boolean => this.compass.getScoreGroup(guid) != null)
          .map((guid: string) => [guid, this.compass.getScoreGroup(guid).maxScoreDependency])
      );
      let shouldUpdateGroupMemberDependency: boolean = false;
      if (this.trackable) {
        unselected.forEach((guid: string) => {
          this.trackable.scoreGroupMemberDependency.subtract(guid, false);
          if (!shouldUpdateGroupMemberDependency)
            shouldUpdateGroupMemberDependency = true;
          this.compass?.getScoreGroup(guid)?.maxScoreDependency.removeGroup(this.statusField.getDependencyId());
        });
      }
      if (selected.length > 0) {
        if (this.trackable) {
          selected.forEach((guid: string) => {
            this.trackable.scoreGroupMemberDependency.add(guid, true);
            if (!shouldUpdateGroupMemberDependency)
              shouldUpdateGroupMemberDependency = true;
          });
        }
        this.scaleRanges.forEach((scaleRange: TrackableScaleRangeGroup) => {
          scaleRange.updateMaxScoreContribution();
        });
      }
      if (shouldUpdateGroupMemberDependency)
        this.trackable?.scoreGroupMemberDependency.update();
    });
    this.scoreGroupOptionsSubscription = this.compass?.scoreGroupOptions.optionsChanged.subscribe((change: SelectOptionsChange<string>) => {
      this.groupGuids.value = change.options.length == 1 ? [change.options[0].value] : this.groupGuids.value.filter((guid: string): boolean => change.options.findIndex((option: SelectOption<string>): boolean => option.value == guid) >= 0);
    });
    this.scaleType = new SavedSelectField<CompassTrackableScaleType>(this.getScaleType(from), null, this.updators, [this.accessControl],
      null, true, null, null, new SelectOptions<CompassTrackableScaleType>([
        { value: CompassTrackableScaleType.AVERAGE, displayValue: 'Average' },
        { value: CompassTrackableScaleType.COUNT, displayValue: 'Count' },
        { value: CompassTrackableScaleType.SUM, displayValue: 'Sum' }
      ]), (): boolean => {
        return isEditable() && (!this.compass || this.compass.isOriginal() || !this.accessControl.has(CompassAccessControl.TRACKABLE_SCALE_EDITING_ALLOWED));
      }, (oldValue: CompassTrackableScaleType, newValue: CompassTrackableScaleType, field: SavedSelectField<CompassTrackableScaleType>): void => {
        if (newValue == oldValue)
          return;
        if (newValue == null || oldValue == null || newValue == CompassTrackableScaleType.COUNT || oldValue == CompassTrackableScaleType.COUNT) {
          this.clearScaleRanges();
          if (!this.compass.usesCustomScoreClass() && newValue == field.savedValue)
            this.setScaleRanges(this.savedScaleRanges, field);
        }
      }
    );
    this.compass.usesCustomScoreClass() ? this.clearScaleRanges() : this.setScaleRanges(this.savedScaleRanges);
    if (!this.isNew)
      this.touchAll(true);
    this.fieldMap = {
      name: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      groupGuids: { name: '' },
      scale: { name: 'scaleRanges' }
    };
  }

  private clearScaleRanges(shouldUpdate: boolean = true) {
    this.scaleRanges.forEach((range: TrackableScaleRangeGroup) => range.destroy());
    this.scaleRanges = [];
    this.rangeIdToIndexMap.clear();
    if (shouldUpdate)
      this.statusField?.update();
  }

  setScaleRanges(scaleRanges: TrackableScaleRange[], scaleTypeField: SavedSelectField<CompassTrackableScaleType> = this.scaleType) {
    if (scaleRanges?.length > 0) {
      let i = 0;
      const minLength: number = Math.min(scaleRanges.length, this.scaleRanges.length);
      while (i < minLength) {
        this.scaleRanges[i].updateFrom(scaleRanges[i]);
        i++;
      }
      while (i < scaleRanges.length) {
        this.addScaleRange(scaleRanges[i], scaleTypeField), false;
        i++;
      }
      while (i < this.scaleRanges.length)
        this.removeScaleRange(this.scaleRanges[i].id, false);
    } else
      this.clearScaleRanges(false);
    this.statusField?.update();
  }

  destroy() {
    this.groupGuids.value = [];
    if (this.scoreGroupOptionsSubscription)
      this.scoreGroupOptionsSubscription.unsubscribe();
    this.detach();
  }

  changeTo(scale: TrackableScaleRange[]) {
    if (this.compass.usesCustomScoreClass() || scale.length == 0 || !this.canEditScale())
      return;
    this.scaleType.savedValue = scale[0].type;
    this.scaleType.reset();
    this.clearScaleRanges();
    this.setScaleRanges(scale);
    this.scaleRanges.forEach((rangeGroup: TrackableScaleRangeGroup) => {
      rangeGroup.isNew = true;
      rangeGroup.touchAll();
    });
    this.statusField?.update();
  }

  updateFrom(from: TrackableValuation, forceNew?: boolean) {
    this.isNew = forceNew != null ? forceNew : from == null;
    this.trackableName = from?.trackableName;
    this.groupGuids.savedValue = from?.groupGuids || [];
    this.scaleType.savedValue = this.getScaleType(from);
    this.savedScaleRanges = this.getScaleRanges(from);
    this.scaleRanges.forEach((scaleRange: TrackableScaleRangeGroup, index: number) => scaleRange.updateFrom(this.savedScaleRanges[index]));
    this.statusField?.update();
    if (!this.isNew)
      this.touchAll(true);
  }

  touchAll(value: boolean = true) {
    this.statusField.touch(value);
    this.groupGuids.touch(value);
    this.scaleType.touch(value);
    this.scaleRanges.forEach((rangeGroup: TrackableScaleRangeGroup) => rangeGroup.touchAll(value));
  }

  resetAll() {
    this.groupGuids.reset();
    this.scaleType.reset();
    this.setScaleRanges(this.savedScaleRanges);
    this.scaleRanges.forEach((rangeGroup: TrackableScaleRangeGroup) => rangeGroup.resetAll());
    this.statusField.reset();
    this.statusField?.update();
  }

  getSaved(trackableName: string = null): TrackableValuation {
    return new TrackableValuation({
      trackableName: trackableName || this.trackableName,
      groupGuids: this.groupGuids.value,
      scaleType: this.scaleType.value,
      scale: this.scaleRanges.map((rangeGroup: TrackableScaleRangeGroup): TrackableScaleRange => rangeGroup.getSaved(this.scaleType.value))
    });
  }

  getScaleRanges(from?: TrackableValuation): TrackableScaleRange[] {
    return from?.scale || [];
  }

  getScaleType(from?: TrackableValuation): CompassTrackableScaleType {
    if (!from)
      return null;
    if (from.scaleType)
      return from.scaleType;
    const scaleRanges: TrackableScaleRange[] = this.getScaleRanges(from);
    return scaleRanges.length > 0 ? scaleRanges[0].type : null;
  }

  addScaleRange(from?: TrackableScaleRange, scaleTypeField: SavedSelectField<CompassTrackableScaleType> = this.scaleType, shouldUpdate: boolean = true) {
    const range: TrackableScaleRangeGroup = new TrackableScaleRangeGroup(from, scaleTypeField.value, this.scaleRangesGroup.updators, this.rangesDependency, this.rangeIdToIndexMap, this.statusField.getDependencyId(), this.accessControl, this);
    this.rangeIdToIndexMap.set(range.id, this.scaleRanges.length);
    this.scaleRanges.push(range);
    if (shouldUpdate)
      this.statusField?.update();
  }

  removeScaleRange(id: number, shouldUpdate: boolean = true) {
    const index: number = this.rangeIdToIndexMap.get(id);
    if (index == null)
      return;
    this.scaleRanges[index].destroy();
    this.scaleRanges.splice(index, 1);
    for (let i = index; i < this.scaleRanges.length; i++)
      this.rangeIdToIndexMap.set(this.scaleRanges[i].id, i);
    this.rangeIdToIndexMap.delete(id);
    if (shouldUpdate)
      this.statusField?.update();
  }

  canRemove(): boolean {
    return !this.compass || this.compass.isOriginal() || !this.accessControl.has(CompassAccessControl.TRACKABLE_DELETION_ALLOWED);
  }

  canEditScale(): boolean {
    return !this.compass || this.compass.isOriginal() || !this.accessControl.has(CompassAccessControl.TRACKABLE_SCALE_EDITING_ALLOWED);
  }
}

export class TrackableScaleRangeGroup extends SavedGroupStatus implements FieldMap {
  private static nextId: number = 1;

  scale: CompassTrackableScaleGroup;
  trackable: CompassTrackableGroup;
  compass: SavedCompass;
  trackableAccessControl: SavedAccessControl;
  rangesDependency: RangeOverlapDependency;
  type: CompassTrackableScaleType;
  id: number;
  weight: SavedNumberField;
  minimum: SavedNumberField;
  maximum: SavedNumberField;
  countMinimum: SavedNumberField;
  countMaximum: SavedNumberField;
  statusField: SimpleStatusField;
  isNew: boolean;
  sumGroupId: number;
  fieldMap: FieldMapType;

  constructor(from: TrackableScaleRange, type: CompassTrackableScaleType, parentUpdators: CountUpdators,
    rangesDependency: RangeOverlapDependency, rangeIdToIndexMap: Map<number, number>, sumGroupId?: number,
    trackableAccessControl?: SavedAccessControl, scale?: CompassTrackableScaleGroup) {
    super(parentUpdators);
    this.isNew = from == null;
    this.scale = scale;
    this.trackable = scale?.trackable;
    this.compass = this.trackable?.compass;
    this.trackableAccessControl = trackableAccessControl;
    this.rangesDependency = rangesDependency;
    this.sumGroupId = sumGroupId;
    this.type = type;
    this.id = TrackableScaleRangeGroup.nextId++;
    this.statusField = new SimpleStatusField(this.updators, [rangesDependency], [() => {
      const overlappingIds: number[] = rangesDependency.getOverlapping(this.id).sort();
      return overlappingIds.length == 0 ? null : 'Overlaps with range' + (overlappingIds.length == 1 ? ' ' : 's ')
        + overlappingIds.map((rangeId: number, index: number, ranges: number[]): string =>
          ((index > 0 && index == ranges.length - 1) ? 'and ' : '') + (RangeID.fromRangeIndex(rangeIdToIndexMap.get(rangeId))).toString())
          .join(overlappingIds.length > 2 ? ', ' : ' ');
    }]);
    const accessControlDependencies: Dependency[] = this.trackableAccessControl ? [this.trackableAccessControl] : [];
    const isEditable: () => boolean = (): boolean => !this.compass || this.compass.isEditing() || this.compass.isEditing(this.trackable);
    this.weight = new SavedNumberField(from ? from.weight?.toString() : null, null, this.updators, [...accessControlDependencies], null, (): boolean => {
      return isEditable() && (!this.compass || this.compass.isOriginal() || !this.trackableAccessControl?.has(CompassAccessControl.TRACKABLE_SCALE_EDITING_ALLOWED));
    }, () => this.updateMaxScoreContribution(), true,
      0, null, true);
    this.minimum = new SavedNumberField(from ? from.minimum?.toString() : null, null, this.updators, [...accessControlDependencies],
      [(field: SavedNumberField): string =>
        (field.value != null && this.maximum && this.maximum.value != null && parseFloat(field.value) >= parseFloat(this.maximum.value))
          ? 'Must be less than the maximum' : null], (): boolean => {
            return isEditable() && (!this.compass || this.compass.isOriginal() || !this.trackableAccessControl?.has(CompassAccessControl.TRACKABLE_SCALE_EDITING_ALLOWED));
          }, (oldValue: string, newValue: string): void => {
            if (newValue != oldValue)
              this.updateRangeDependency();
          }, true, null, null, false
    );
    this.maximum = new SavedNumberField(from ? from.maximum?.toString() : null, null, this.updators, [this.minimum, ...accessControlDependencies],
      [(field: SavedNumberField): string =>
        (field.value != null && this.minimum && this.minimum.value != null && parseFloat(field.value) <= parseFloat(this.minimum.value))
          ? 'Must be greater than the minimum' : null], (): boolean => {
            return isEditable() && (!this.compass || this.compass.isOriginal() || !this.trackableAccessControl?.has(CompassAccessControl.TRACKABLE_SCALE_EDITING_ALLOWED));
          }, (oldValue: string, newValue: string): void => {
            if (newValue != oldValue)
              this.updateRangeDependency();
          }, true, null, null, false
    );
    this.minimum.addDependencies(this.maximum);
    this.countMinimum = new SavedNumberField(from ? from.countMinimum?.toString() : (this.isCount() ? null : '0'), null, this.updators, [...accessControlDependencies], [
      (field: SavedNumberField): string =>
        (field.value != null && this.countMaximum && this.countMaximum.value != null && parseInt(field.value) > parseInt(this.countMaximum.value))
          ? 'Cannot be greater than the count maximum' : null
    ], (): boolean => {
      return isEditable() && (!this.compass || this.compass.isOriginal() || !this.trackableAccessControl?.has(CompassAccessControl.TRACKABLE_SCALE_EDITING_ALLOWED));
    }, (oldValue: string, newValue: string): void => {
      if (newValue != oldValue)
        this.updateRangeDependency();
    }, this.isCount(), 0, null, true);
    this.countMaximum = new SavedNumberField(from ? from.countMaximum?.toString() : (this.isCount() ? null : '0'), null, this.updators, [this.countMinimum, ...accessControlDependencies], [
      (field: SavedNumberField): string =>
        (field.value != null && this.countMinimum && this.countMinimum.value != null && parseInt(field.value) < parseInt(this.countMinimum.value))
          ? 'Cannot be less than the count minimum' : null
    ], (): boolean => {
      return isEditable() && (!this.compass || this.compass.isOriginal() || !this.trackableAccessControl?.has(CompassAccessControl.TRACKABLE_SCALE_EDITING_ALLOWED));
    }, (oldValue: string, newValue: string): void => {
      if (newValue != oldValue)
        this.updateRangeDependency();
    }, this.isCount(), 0, null, true);
    this.countMinimum.addDependencies(this.countMaximum);
    this.updateRangeDependency();
    if (!this.isNew)
      this.touchAll(true);
    this.fieldMap = {
      name: {
        name: 'weight', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MIN: FieldMapHandlerStrings.DEFAULT,
          FMT: FieldMapHandlerStrings.DEFAULT
        }
      },
      weight: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MIN: FieldMapHandlerStrings.DEFAULT,
          FMT: FieldMapHandlerStrings.DEFAULT
        }
      },
      minimum: { name: '' },
      maximum: {
        name: '', handlers: {
          FMT: FieldMapHandlerStrings.DEFAULT
        }
      },
      countMinimum: {
        name: '', handlers: {
          MIN: FieldMapHandlerStrings.DEFAULT
        }
      },
      countMaximum: {
        name: '', handlers: {
          MIN: FieldMapHandlerStrings.DEFAULT,
          FMT: FieldMapHandlerStrings.DEFAULT
        }
      }
    };
  }

  setMaxScoreContribution(contribution: number) {
    this.scale?.maxScoreDependencies.forEach((dependency: SumContributionGroupsDependency) => dependency.setContribution(this.sumGroupId, this.statusField.getDependencyId(), contribution));
  }

  updateMaxScoreContribution() {
    const weight: number = parseInt(this.weight?.value);
    this.setMaxScoreContribution((weight != null && weight >= 0) ? weight : null);
  }

  clearMaxScoreContribution() {
    this.setMaxScoreContribution(null);
  }

  destroy() {
    this.rangesDependency.delete(this.id);
    this.clearMaxScoreContribution();
    this.detach();
  }

  updateRangeDependency() {
    const range: Range = this.type == CompassTrackableScaleType.COUNT
      ? new SquareRange(this.id,
        { value: parseFloat(this.minimum?.value), inclusive: true }, { value: parseFloat(this.maximum?.value), inclusive: false },
        { value: parseInt(this.countMinimum?.value), inclusive: true }, { value: parseInt(this.countMaximum?.value), inclusive: true })
      : new LineRange(this.id,
        { value: parseFloat(this.minimum?.value), inclusive: true }, { value: parseFloat(this.maximum?.value), inclusive: false });
    this.rangesDependency.set(range);
    this.updateMaxScoreContribution();
  }

  updateFrom(from: TrackableScaleRange) {
    this.isNew = from == null;
    this.weight.savedValue = from ? from.weight?.toString() : null;
    this.minimum.savedValue = from ? from.minimum?.toString() : null;
    this.maximum.savedValue = from ? from.maximum?.toString() : null;
    this.countMinimum.savedValue = from ? from.countMinimum?.toString() : (this.isCount() ? null : '0');
    this.countMaximum.savedValue = from ? from.countMaximum?.toString() : (this.isCount() ? null : '0');
    if (!this.isNew)
      this.touchAll(true);
  }

  touchAll(value: boolean = true) {
    this.weight.touch(value);
    this.minimum.touch(value);
    this.maximum.touch(value);
    this.countMinimum.touch(value);
    this.countMaximum.touch(value);
    this.statusField.touch(value);
  }

  resetAll() {
    this.weight.reset();
    this.minimum.reset();
    this.maximum.reset();
    this.countMinimum.reset();
    this.countMaximum.reset();
    this.statusField.reset();
    this.statusField?.update();
  }

  getSaved(scaleType: CompassTrackableScaleType): TrackableScaleRange {
    return new TrackableScaleRange({
      type: scaleType,
      weight: (this.weight.value != null && this.weight.value.length > 0) ? parseInt(this.weight.value) : null,
      minimum: (this.minimum.value != null && this.minimum.value.length > 0) ? parseFloat(this.minimum.value) : null,
      maximum: (this.maximum.value != null && this.maximum.value.length > 0) ? parseFloat(this.maximum.value) : null,
      countMinimum: (this.countMinimum.value != null && this.countMinimum.value.length > 0) ? parseInt(this.countMinimum.value) : null,
      countMaximum: (this.countMaximum.value != null && this.countMaximum.value.length > 0) ? parseInt(this.countMaximum.value) : null,
    });
  }

  isCount(): boolean {
    return this.type === CompassTrackableScaleType.COUNT;
  }

  isSum(): boolean {
    return this.type === CompassTrackableScaleType.SUM;
  }

  touchMinMax(field: SavedNumberField) {
    if (this.minimum.value != null && this.maximum.value != null) {
      this.minimum.touch();
      this.maximum.touch();
    } else if (field.problem != null)
      field.touch();
    this.statusField.touch();
  }

  touchCountMinMax(field: SavedNumberField) {
    if (this.countMinimum.value != null && this.countMaximum.value != null) {
      this.countMinimum.touch();
      this.countMaximum.touch();
    } else if (field.problem != null)
      field.touch();
    this.statusField.touch();
  }
}

export class CompassAnswerableGroup extends SavedGroupStatus implements CompassEditable, FieldMap, Collapsible {
  accessControl: SavedAccessControl;
  question: SavedTextField;
  parentQuestionNumber: SavedNumberField;
  caredForQuestion: SavedTextField;
  answerType: SavedSelectField<CompassAnswerableType>;
  multiAnswer: SavedSelectField<boolean>;
  buttonsGroup: SavedGroupStatus;
  buttons: CompassAnswerableButtonGroup[];
  savedButtons: CompassAnswerableButton[];
  statusField: SimpleStatusField;
  isNew: boolean;
  buttonTextDependency: MapCountDependency<string>;
  compass: SavedCompass;
  fieldMap: FieldMapType;
  expanded: boolean;

  constructor(from: CompassAnswerable, parentUpdators: CountUpdators, compass?: SavedCompass, forceNew: boolean = false, adaptGroupGUIDs: boolean = false, requireQuestion: boolean = true) {
    super(parentUpdators);
    if (adaptGroupGUIDs) {
      from.answers.forEach((answer: CompassAnswerableButton) => {
        if (answer.weights.length > 0) {
          answer.weights = [answer.weights[0]];
          answer.weights[0].groupGuids = (compass?.scoreGroups || []).map((group: CompassScoreGroupGroup): string => group.guid);
        }
      });
    }
    this.expanded = false;
    this.isNew = forceNew || from == null;
    this.compass = compass;
    this.accessControl = new SavedAccessControl(from?.accessControl);
    this.compass?.answerableStatusField.addDependencies(this.accessControl);
    this.buttonTextDependency = new MapCountDependency<string>();
    this.buttonsGroup = new SavedGroupStatus(this.updators);
    this.buttons = [];
    this.savedButtons = from?.answers || [];
    this.statusField = new SimpleStatusField(this.buttonsGroup.updators, null, [(): string =>
      (this.isButton() && this.buttons.length < 2 && !this.parentQuestionNumber) ? 'Must provide at least two buttons' : null], (): boolean =>
      this.buttons.length != this.savedButtons.length || this.buttons.findIndex((button: CompassAnswerableButtonGroup): boolean => button.isNew) != -1);
    const isEditable: () => boolean = (): boolean => !this.compass || this.compass.isEditing() || this.compass.isEditing(this);
    this.question = new SavedTextField(from ? from.question : null, null, this.updators, [this.accessControl], null, (): boolean => {
      return isEditable() && (!this.compass || this.compass.isOriginal() || !this.accessControl.has(CompassAccessControl.ASSESSMENT_QUESTION_TEXT_EDITING_ALLOWED));
    }, null, requireQuestion);
    this.caredForQuestion = new SavedTextField(from ? from.caredForQuestion : null, null, this.updators, [this.accessControl], null, (): boolean => {
      return isEditable() && (!this.compass || this.compass.isOriginal() || !this.accessControl.has(CompassAccessControl.ASSESSMENT_QUESTION_TEXT_EDITING_ALLOWED));
    }, null, requireQuestion);
    this.parentQuestionNumber = new SavedNumberField(from && from.parentQuestionNumber ? from.parentQuestionNumber.toString() : null, null, this.updators, null,null, () => true,  (oldValue: string, newValue: string, field: SavedNumberField): void => {
      if (newValue != oldValue && this.parentQuestionNumber) {
        this.parentQuestionNumber.value = newValue;
      }
    },  false, null,  null );
    this.answerType = new SavedSelectField<CompassAnswerableType>(from ? from.answerType : null, null, this.updators, null,
      null, true, null, null, new SelectOptions<CompassAnswerableType>([
        { value: CompassAnswerableType.BUTTON, displayValue: 'Button' },
        { value: CompassAnswerableType.DATE, displayValue: 'Date' },
        { value: CompassAnswerableType.INPUT, displayValue: 'Input' },
        { value: CompassAnswerableType.PARENT, displayValue: 'Parent Question' }
      ]), isEditable, (oldValue: CompassAnswerableType, newValue: CompassAnswerableType, field: SavedSelectField<CompassAnswerableType>): void => {
        if (newValue != oldValue) {
          if (newValue === CompassAnswerableType.BUTTON) {
            this.multiAnswer?.reset();
            this.clearButtons();
            this.setButtons(this.savedButtons);
          } else if (oldValue === CompassAnswerableType.BUTTON) {
            if (this.multiAnswer)
              this.multiAnswer.value = false;
            this.clearButtons();
          }
        }
      }
    );
    this.statusField.addDependencies(this.answerType);
    this.multiAnswer = new SavedSelectField<boolean>(from?.multiAnswer || false, null, this.updators, [this.answerType],
      null, null, null, null, new SelectOptions<boolean>(
        [{ value: true, displayValue: 'Yes' }, { value: false, displayValue: 'No' }],
        (a: SelectOption<boolean>, b: SelectOption<boolean>): number => (b.value ? 1 : 0) - (a.value ? 1 : 0)
      ), isEditable, (_, newValue: boolean) =>
      this.compass?.scoreGroups.forEach((group: CompassScoreGroupGroup) => group.maxScoreDependency?.setGroupType(this.statusField.getDependencyId(), newValue ? ContributionGroupAggregationType.SUM : ContributionGroupAggregationType.FIRST)));
    this.setButtons(this.savedButtons);
    if (!this.isNew)
      this.touchAll(true);
    this.fieldMap = {
      question: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT
        }
      },
      caredForQuestion: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT
        }
      },
      answerType: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT
        }
      },
      parentQuestionNumber: {
        name: ''
      },
      multiAnswer: { name: '' },
      answers: { name: 'buttons' }
    };
  }

  getEditId(): number {
    return this.question.getDependencyId();
  }

  private clearButtons(shouldUpdate: boolean = true) {
    this.buttons.forEach((button: CompassAnswerableButtonGroup) => button.destroy());
    this.buttons = [];
    if (shouldUpdate)
      this.statusField?.update();
  }

  private setButtons(buttons: CompassAnswerableButton[]) {
    if (buttons?.length > 0) {
      let i = 0;
      const minLength: number = Math.min(buttons.length, this.buttons.length);
      while (i < minLength) {
        this.buttons[i].updateFrom(buttons[i]);
        i++;
      }
      while (i < buttons.length) {
        this.addButton(buttons[i]), false;
        i++;
      }
      while (i < this.buttons.length)
        this.removeButton(i, false);
    } else
      this.clearButtons(false);
    this.statusField?.update();
  }

  destroy() {
    if (this.compass?.isEditing(this))
      this.compass.edit(null);
    this.buttons.forEach((button: CompassAnswerableButtonGroup) => button.destroy());
    this.compass?.answerableStatusField.removeDependency(this.accessControl);
    this.detach();
  }

  changeTo(answerable: CompassAnswerable, expanded: boolean) {
    this.isNew = false;
    this.accessControl.setList(answerable.accessControl);
    this.question.value = answerable.question || null;
    this.caredForQuestion.value = answerable.caredForQuestion || null;
    this.answerType.value = answerable.answerType || null;
    this.multiAnswer.value = answerable.multiAnswer || false;
    this.parentQuestionNumber.value = answerable.parentQuestionNumber?.toString() || null;
    this.setButtons(answerable.answers || []);
    this.statusField?.update();
    this.compass?.answerableStatusField?.update();
    this.touchAll(true);
    this.expanded = expanded;
  }

  updateFrom(from: CompassAnswerable) {
    this.isNew = from == null;
    this.accessControl.setList(from?.accessControl);
    this.question.savedValue = from ? from.question : null;
    this.caredForQuestion.savedValue = from ? from.caredForQuestion : null;
    this.answerType.savedValue = from ? from.answerType : null;
    this.multiAnswer.savedValue = from?.multiAnswer || false;
    this.parentQuestionNumber.savedValue = from?.parentQuestionNumber?.toString() || null;
    this.savedButtons = from?.answers || [];
    this.buttons.forEach((button: CompassAnswerableButtonGroup, index: number) => button.updateFrom(this.savedButtons[index]));
    this.statusField?.update();
    if (!this.isNew)
      this.touchAll(true);
  }

  touchAll(value: boolean = true) {
    this.statusField.touch(value);
    this.question.touch(value);
    this.caredForQuestion.touch(value);
    this.answerType.touch(value);
    this.multiAnswer.touch(value);
    this.parentQuestionNumber.touch(value);
    this.buttons.forEach((buttonGroup: CompassAnswerableButtonGroup) => buttonGroup.touchAll(value));
  }

  resetAll() {
    this.question.reset();
    this.caredForQuestion.reset();
    this.answerType.reset();
    this.multiAnswer.reset();
    this.setButtons(this.savedButtons);
    this.buttons.forEach((buttonGroup: CompassAnswerableButtonGroup) => buttonGroup.resetAll());
    this.statusField.reset();
    this.parentQuestionNumber.reset();
    this.statusField?.update();
  }

  getSaved(): CompassAnswerable {
    return new CompassAnswerable({
      accessControl: this.accessControl.getList(),
      question: this.question.value,
      caredForQuestion: this.caredForQuestion.value,
      parentQuestionNumber: +this.parentQuestionNumber.value,
      answerType: this.answerType.value,
      multiAnswer: this.isButton() ? this.multiAnswer.value : null,
      answers: this.buttons.map((buttonGroup: CompassAnswerableButtonGroup): CompassAnswerableButton => buttonGroup.getSaved())
    });
  }

  addButton(from?: CompassAnswerableButton, shouldUpdate: boolean = true) {
    const button: CompassAnswerableButtonGroup = new CompassAnswerableButtonGroup(from, this.buttonsGroup.updators, this.statusField.getDependencyId(), this.buttonTextDependency, this, this.accessControl);
    this.buttons.push(button);
    if (shouldUpdate)
      this.statusField?.update();
  }

  removeButton(index: number, shouldUpdate: boolean = true) {
    this.buttons[index].destroy();
    this.buttons.splice(index, 1);
    if (shouldUpdate)
      this.statusField?.update();
  }

  isButton(): boolean {
    return this.answerType?.value === CompassAnswerableType.BUTTON;;
  }

  canRemove(): boolean {
    return !this.compass || this.compass.isOriginal() || !this.accessControl.has(CompassAccessControl.ASSESSMENT_QUESTION_DELETION_ALLOWED);
  }

  canAddRemoveButtons(): boolean {
    return !this.compass || this.compass.isOriginal() || !(this.accessControl.has(CompassAccessControl.ASSESSMENT_QUESTION_ANSWER_EDITING_ALLOWED) || this.accessControl.has(CompassAccessControl.ASSESSMENT_QUESTION_SCALE_EDITING_ALLOWED));
  }
}

export class CompassAnswerableButtonGroup extends SavedGroupStatus implements FieldMap, Collapsible {
  answer: SavedTextField;
  isNew: boolean;
  statusField: SimpleStatusField;
  sumGroupId: number;
  flow: SavedNumberField;
  weightsGroup: SavedGroupStatus;
  weights: CompassAnswerableButtonWeightGroup[];
  savedWeights: CompassAnswerableButtonWeight[];
  textDependency: MapCountDependency<string>;
  scoreGroupMemberDependency: MapCountDependency<string>;
  scoreGroupOptionsSubscription: Subscription;
  answerable: CompassAnswerableGroup;
  compass: SavedCompass;
  answerableAccessControl: SavedAccessControl;
  fieldMap: FieldMapType;
  expanded: boolean;

  constructor(from: CompassAnswerableButton, parentUpdators: CountUpdators, sumGroupId: number,
    textDependency: MapCountDependency<string>, answerable?: CompassAnswerableGroup, answerableAccessControl?: SavedAccessControl) {
    super(parentUpdators);
    this.expanded = false;
    this.isNew = from == null;
    this.answerable = answerable;
    this.compass = this.answerable?.compass;
    this.answerableAccessControl = answerableAccessControl;
    this.sumGroupId = sumGroupId;
    this.textDependency = textDependency;
    this.scoreGroupMemberDependency = new MapCountDependency<string>();
    this.weightsGroup = new SavedGroupStatus(this.updators);
    this.weights = [];
    this.savedWeights = from?.weights || [];
    this.scoreGroupOptionsSubscription = this.compass?.scoreGroupOptions.optionsChanged.subscribe((change: SelectOptionsChange<string>) => {
      if (change.options.length == 1 && this.weights.length > 0) {
        const guid: string = change.options[0].value;
        let keepIndex: number = this.weights.findIndex((weight: CompassAnswerableButtonWeightGroup): boolean => (weight.groupGuids.value || []).findIndex((g: string): boolean => g == guid) >= 0);
        if (keepIndex < 0)
          keepIndex = 0;
        const length: number = this.weights.length;
        for (let i = 0; i < length; i++) {
          if (i != keepIndex)
            this.removeWeight(i < keepIndex ? 0 : 1, false);
        }
        this.statusField?.update();
      }
    });
    this.statusField = new SimpleStatusField(this.updators, null, null, (): boolean =>
      this.weights.length != this.savedWeights.length || this.weights.findIndex((weight: CompassAnswerableButtonWeightGroup): boolean => weight.isNew) != -1);
    const accessControlDependencies: Dependency[] = answerableAccessControl ? [answerableAccessControl] : [];
    const isEditable: () => boolean = (): boolean => !this.compass || this.compass.isEditing() || this.compass.isEditing(this.answerable);
    this.answer = new SavedTextField(from ? from.answer : null, null, this.updators, [textDependency, ...accessControlDependencies],
      [(field: SavedTextField) => textDependency.count(field.value) > 1 ? 'Duplicate' : null], (): boolean => {
        return isEditable() && (!this.compass || this.compass.isOriginal() || !this.answerableAccessControl?.has(CompassAccessControl.ASSESSMENT_QUESTION_ANSWER_EDITING_ALLOWED));
      }, (oldValue: string, newValue: string): void => {
        if (oldValue != null && newValue != null)
          textDependency.replace(oldValue, newValue);
        else if (oldValue != null)
          textDependency.subtract(oldValue);
        else if (newValue != null)
          textDependency.add(newValue);
      }, true
    );
    this.flow = new SavedNumberField(from && from.flow ? from.flow.toString() : null, null, this.updators, null,null, () => true,  (oldValue: string, newValue: string, field: SavedNumberField): void => {
      if (this.flow && newValue != oldValue) {
        this.flow.value = newValue;
      }
    },  false, 1,  null );
    this.setWeights(this.savedWeights);
    if (!this.isNew)
      this.touchAll(true);
    this.fieldMap = {
      answer: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT
        }
      },
      weights: { name: '' }
    };
  }

  clearWeights(shouldUpdate: boolean = true) {
    this.weights.forEach((weight: CompassAnswerableButtonWeightGroup) => weight.destroy());
    this.weights = [];
    if (shouldUpdate)
      this.statusField?.update();
  }

  setWeights(weights: CompassAnswerableButtonWeight[]) {
    if (weights?.length > 0) {
      let i = 0;
      const minLength: number = Math.min(weights.length, this.weights.length);
      while (i < minLength) {
        this.weights[i].updateFrom(weights[i]);
        i++;
      }
      while (i < weights.length) {
        this.addWeight(weights[i]), false;
        i++;
      }
      while (i < this.weights.length)
        this.removeWeight(i, false);
    } else
      this.clearWeights(false);
    this.statusField?.update();
  }

  destroy() {
    if (this.answer.value != null)
      this.textDependency.subtract(this.answer.value);
    this.weights.forEach((weight: CompassAnswerableButtonWeightGroup) => weight.destroy());
    if (this.scoreGroupOptionsSubscription)
      this.scoreGroupOptionsSubscription.unsubscribe();
    this.detach();
  }

  updateFrom(from: CompassAnswerableButton) {
    this.isNew = from == null;
    this.answer.savedValue = from ? from.answer : null;
    this.savedWeights = from?.weights || [];
    this.flow.savedValue = from && from.flow ? from.flow.toString() : null;
    this.weights.forEach((weight: CompassAnswerableButtonWeightGroup, index: number) => weight.updateFrom(this.savedWeights[index]));
    this.statusField?.update();
    if (!this.isNew)
      this.touchAll(true);
  }

  touchAll(value: boolean = true) {
    this.statusField.touch(value);
    this.answer.touch(value);
    this.flow.touch(value);
    this.weights.forEach((weight: CompassAnswerableButtonWeightGroup) => weight.touchAll(value));
  }

  resetAll() {
    this.answer.reset();
    this.setWeights(this.savedWeights);
    this.weights.forEach((weight: CompassAnswerableButtonWeightGroup) => weight.resetAll());
    this.statusField.reset();
    this.flow.reset();
    this.statusField?.update();
  }

  getSaved(): CompassAnswerableButton {
    return new CompassAnswerableButton({
      answer: this.answer.value,
      flow: this.flow.value,
      weights: this.weights.map((weight: CompassAnswerableButtonWeightGroup): CompassAnswerableButtonWeight => weight.getSaved())
    });
  }

  addWeight(from?: CompassAnswerableButtonWeight, shouldUpdate: boolean = true) {
    const weight: CompassAnswerableButtonWeightGroup = new CompassAnswerableButtonWeightGroup(from, this.weightsGroup.updators, this);
    this.weights.push(weight);
    if (shouldUpdate)
      this.statusField?.update();
  }

  removeWeight(index: number, shouldUpdate: boolean = true) {
    this.weights[index].destroy();
    this.weights.splice(index, 1);
    if (shouldUpdate)
      this.statusField?.update();
  }
}

export class CompassAnswerableButtonWeightGroup extends SavedGroupStatus implements FieldMap {
  groupGuids: SavedMultiselectField<string>;
  value: SavedNumberField;
  statusField: SimpleStatusField;
  isNew: boolean;
  sumGroupId: number;
  maxScoreDependencies: Map<string, SumContributionGroupsDependency>;
  button: CompassAnswerableButtonGroup;
  scoreGroupOptionsSubscription: Subscription;
  compass: SavedCompass;
  answerableAccessControl: SavedAccessControl;
  fieldMap: FieldMapType;

  constructor(from: CompassAnswerableButtonWeight, parentUpdators: CountUpdators, button: CompassAnswerableButtonGroup, forceNew: boolean = false) {
    super(parentUpdators);
    this.isNew = forceNew || from == null;
    this.button = button;
    this.compass = button.compass;
    this.answerableAccessControl = button.answerableAccessControl;
    this.sumGroupId = button.sumGroupId;
    this.statusField = new SimpleStatusField(parentUpdators);
    const accessControlDependencies: Dependency[] = this.answerableAccessControl ? [this.answerableAccessControl] : [];
    const isEditable: () => boolean = (): boolean => !this.button.compass || this.button.compass.isEditing() || this.compass.isEditing(this.button.answerable);
    this.groupGuids = new SavedMultiselectField<string>(from?.groupGuids || [], null, this.updators, accessControlDependencies.concat([this.button.scoreGroupMemberDependency]), [
      (field: SavedMultiselectField<string>): string => {
        return ((field.value || []).findIndex((guid: string): boolean => this.button.scoreGroupMemberDependency.count(guid) > 1) >= 0) ? 'Duplicate' : ''
      }
    ], button?.compass != null, null, null, this.compass?.scoreGroupOptions || new SelectOptions<string>(), (): boolean => {
      return isEditable() && (!this.compass || this.compass.isOriginal() || !this.answerableAccessControl.has(CompassAccessControl.ASSESSMENT_QUESTION_SCALE_EDITING_ALLOWED));
    }, (_: string[], newValue: string[], __: SavedMultiselectField<string>, selected: string[], unselected: string[]): void => {
      if (!this.compass || (selected.length == 0 && unselected.length == 0))
        return;
      this.maxScoreDependencies = new Map<string, SumContributionGroupsDependency>(
        newValue
          .filter((guid: string): boolean => this.compass.getScoreGroup(guid) != null)
          .map((guid: string) => [guid, this.compass.getScoreGroup(guid).maxScoreDependency])
      );
      let shouldUpdateGroupMemberDependency: boolean = false;
      unselected.forEach((guid: string) => {
        this.button.scoreGroupMemberDependency.subtract(guid, false);
        if (!shouldUpdateGroupMemberDependency)
          shouldUpdateGroupMemberDependency = true;
        const group: CompassScoreGroupGroup = this.compass?.getScoreGroup(guid);
        if (group)
          this.setMaxScoreDependencyContribution(group.maxScoreDependency, null);
      });
      if (selected.length > 0) {
        selected.forEach((guid: string) => {
          this.button.scoreGroupMemberDependency.add(guid, true);
          if (!shouldUpdateGroupMemberDependency)
            shouldUpdateGroupMemberDependency = true;
        });
        this.updateMaxScoreContribution();
      }
      if (shouldUpdateGroupMemberDependency)
        this.button.scoreGroupMemberDependency.update();
    });
    this.scoreGroupOptionsSubscription = this.compass?.scoreGroupOptions.optionsChanged.subscribe((change: SelectOptionsChange<string>) => {
      this.groupGuids.value = change.options.length == 1 ? [change.options[0].value] : this.groupGuids.value.filter((guid: string): boolean => change.options.findIndex((option: SelectOption<string>): boolean => option.value == guid) >= 0);
    });
    this.value = new SavedNumberField(from ? from.weight?.toString() : null, null, this.updators, [...(this.compass ? [this.compass.useCustomScoreClass] : []), ...accessControlDependencies], [
      (field: SavedNumberField): string => (!(this.compass && this.compass.usesCustomScoreClass()) && !(field.value?.length > 0)) ? 'Required' : ''
    ], (): boolean => {
      return isEditable() && (!this.compass || this.compass.isOriginal() || !this.answerableAccessControl?.has(CompassAccessControl.ASSESSMENT_QUESTION_SCALE_EDITING_ALLOWED));
    }, (_: string, newValue: string) => this.updateMaxScoreContribution(newValue), false, 0, null, true);
    if (this.compass?.usesCustomScoreClass())
      this.value.value = '0';
    if (!this.isNew)
      this.touchAll(true);
    this.fieldMap = {
      weight: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT
        }
      },
      groupGuids: { name: '' }
    };
  }

  setMaxScoreDependencyContribution(dependency: SumContributionGroupsDependency, contribution: number) {
    dependency.setContribution(this.sumGroupId, this.statusField.getDependencyId(), contribution)
  }

  setMaxScoreContribution(contribution: number) {
    this.maxScoreDependencies?.forEach((dependency: SumContributionGroupsDependency) => this.setMaxScoreDependencyContribution(dependency, contribution));
  }

  updateMaxScoreContribution(value?: string) {
    const weight: number = parseInt(value == null ? this.value?.value : value);
    this.setMaxScoreContribution((weight != null && weight >= 0) ? weight : null);
  }

  clearMaxScoreContribution() {
    this.setMaxScoreContribution(null);
  }

  destroy() {
    this.clearMaxScoreContribution();
    if (this.scoreGroupOptionsSubscription)
      this.scoreGroupOptionsSubscription.unsubscribe();
    this.detach();
  }

  updateFrom(from: CompassAnswerableButtonWeight, forceNew?: boolean) {
    this.isNew = forceNew != null ? forceNew : from == null;
    this.groupGuids.savedValue = from?.groupGuids || [];
    this.value.savedValue = from ? from.weight?.toString() : null;
    this.statusField?.update();
    if (!this.isNew)
      this.touchAll(true);
  }

  touchAll(value: boolean = true) {
    this.statusField.touch(value);
    this.groupGuids.touch(value);
    this.value.touch(value);
  }

  resetAll() {
    this.groupGuids.reset();
    this.value.reset();
    this.statusField.reset();
    this.statusField?.update();
  }

  getSaved(): CompassAnswerableButtonWeight {
    return new CompassAnswerableButtonWeight({
      groupGuids: this.groupGuids.value,
      weight: (this.value.value != null && this.value.value.length > 0) ? parseInt(this.value.value) : null
    });
  }
}

export class CompassScoreGroupGroup extends SavedGroupStatus implements CompassEditable, FieldMap, Collapsible {
  compass: SavedCompass;
  guid: string;
  name: SavedTextField;
  maxScoreDependency: SumContributionGroupsDependency;
  scaleRangesGroup: SavedGroupStatus;
  scaleRanges: CompassScaleRangeGroup[];
  savedScaleRanges: CompassScaleRange[];
  rangesDependency: RangeOverlapDependency<LineRange>;
  rangeIdToIndexMap: Map<number, number>;
  rangesStatusField: SimpleStatusField;
  fieldMap: FieldMapType;
  expanded: boolean;

  constructor(from: CompassScoreGroup, parentUpdators: CountUpdators, compass: SavedCompass) {
    super(parentUpdators);
    this.expanded = false;
    this.compass = compass;
    this.guid = from?.guid || CompassScoreGroupGroup.generateGUID();
    this.maxScoreDependency = new SumContributionGroupsDependency((a: number, b: number): number => b - a);
    this.maxScoreDependency.updates.subscribe(() => this.updateMaxScoreContribution());
    this.rangeIdToIndexMap = new Map<number, number>();
    this.rangesDependency = new RangeOverlapDependency();
    this.scaleRanges = [];
    this.savedScaleRanges = from?.scaleRanges || [];
    this.scaleRangesGroup = new SavedGroupStatus(this.updators);
    this.rangesStatusField = new SimpleStatusField(this.updators, [this.maxScoreDependency, compass.useCustomScoreClassLinkDependency], [
      (): string => !compass.usesCustomScoreClass() && this.maxScoreDependency.hasContributors() && this.scaleRanges.length == 0 ? 'Must provide at least one scale range' : ''
    ], (): boolean =>
      this.scaleRanges.length != this.savedScaleRanges.length || this.scaleRanges.findIndex((scaleRange: CompassScaleRangeGroup): boolean => scaleRange.isNew) != -1);
    const isEditable: () => boolean = (): boolean => !this.compass || this.compass.isEditing() || this.compass.isEditing(this);
    this.name = new SavedTextField(from?.name, null, this.updators, [this.compass.scoreGroupStatusField, this.compass.scoreGroupNameDependency], [
      (field: SavedTextField): string => (this.compass.scoreGroups.length != 1 && !(field.value?.length > 0)) ? 'Required' : '',
      (field: SavedTextField): string => (field.value && field.value.length > 0 && this.compass.scoreGroupNameDependency.count(field.value) > 1) ? 'Duplicate' : ''
    ], (): boolean => isEditable() && this.compass.scoreGroups.length != 1, (oldValue: string, newValue: string) => {
      if ((oldValue || '') == (newValue || ''))
        return;
      if (oldValue && oldValue.length > 0)
        this.compass.scoreGroupNameDependency.subtract(oldValue);
      if (newValue && newValue.length > 0)
        this.compass.scoreGroupNameDependency.add(newValue);
      this.compass.updateScoreGroupOptions(this.compass.scoreGroups, (newValue && newValue.length > 0) == null);
    }, false, null, 50);
    this.setScaleRanges(this.savedScaleRanges);
    if (!this.isNew())
      this.touchAll(true);
    this.fieldMap = {
      name: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT
        }
      },
      scoreSummaryGroups: { name: '' }
    };
  }

  getEditId(): number {
    return this.name.getDependencyId();
  }

  private clearScaleRanges(shouldUpdate: boolean = true) {
    this.scaleRanges.forEach((range: CompassScaleRangeGroup) => range.destroy());
    this.scaleRanges = [];
    this.rangeIdToIndexMap.clear();
    if (shouldUpdate)
      this.rangesStatusField?.update();
  }

  private setScaleRanges(scaleRanges: CompassScaleRange[]) {
    if (scaleRanges?.length > 0) {
      let i = 0;
      const minLength: number = Math.min(scaleRanges.length, this.scaleRanges.length);
      while (i < minLength) {
        this.scaleRanges[i].updateFrom(scaleRanges[i]);
        i++;
      }
      while (i < scaleRanges.length) {
        this.addScaleRange(scaleRanges[i]), false;
        i++;
      }
      while (i < this.scaleRanges.length)
        this.removeScaleRange(this.scaleRanges[i].id, false);
    } else
      this.clearScaleRanges(false);
    this.rangesStatusField?.update();
  }

  isNew(): boolean {
    return this.compass.savedScoreGroups.find((group: CompassScoreGroup): boolean => group.guid == this.guid) == null;
  }

  changeScaleTo(scale: CompassScaleRange[]) {
    if (this.compass.usesCustomScoreClass() || scale.length == 0 || !this.compass.canAddRemoveScaleRanges())
      return;
    this.clearScaleRanges();
    this.setScaleRanges(scale);
    this.scaleRanges.forEach((rangeGroup: CompassScaleRangeGroup) => {
      rangeGroup.isNew = true;
      rangeGroup.touchAll();
    });
    this.rangesStatusField?.update();
  }

  setMaxScoreContribution(contribution: number) {
    this.compass.maxScoreDependency.setContribution(this.rangesStatusField.getDependencyId(), contribution);
  }

  updateMaxScoreContribution() {
    this.setMaxScoreContribution(this.maxScoreDependency.getSum());
  }

  clearMaxScoreContribution() {
    this.setMaxScoreContribution(null);
  }

  updateFrom(from: CompassScoreGroup) {
    this.guid = from?.guid || CompassScoreGroupGroup.generateGUID();
    this.name.savedValue = from?.name;
    this.savedScaleRanges = from?.scaleRanges || [];
    this.scaleRanges.forEach((scaleRange: CompassScaleRangeGroup, index: number) => scaleRange.updateFrom(this.savedScaleRanges[index]));
    this.rangesStatusField?.update();
    if (!this.isNew())
      this.touchAll(true);
  }

  touchAll(value: boolean = true) {
    this.name.touch(value);
    this.rangesStatusField.touch(value);
    this.scaleRanges.forEach((rangeGroup: CompassScaleRangeGroup) => rangeGroup.touchAll(value));
  }

  resetAll() {
    this.name.reset();
    this.setScaleRanges(this.savedScaleRanges);
    this.scaleRanges.forEach((rangeGroup: CompassScaleRangeGroup) => rangeGroup.resetAll());
    this.rangesStatusField.reset();
    this.rangesStatusField?.update();
  }

  getSaved(): CompassScoreGroup {
    return new CompassScoreGroup({
      guid: this.guid,
      name: this.name.value,
      scaleRanges: this.scaleRanges.map((rangeGroup: CompassScaleRangeGroup): CompassScaleRange => rangeGroup.getSaved())
    });
  }

  destroy() {
    if (this.compass?.isEditing(this))
      this.compass.edit(null);
    if (this.name.value && this.name.value.length > 0)
      this.compass.scoreGroupNameDependency.subtract(this.name.value);
    this.clearMaxScoreContribution();
    this.detach();
  }

  addScaleRange(from?: CompassScaleRange, shouldUpdate: boolean = true) {
    const range: CompassScaleRangeGroup = new CompassScaleRangeGroup(from, this.scaleRangesGroup.updators, this.rangesDependency, this.rangeIdToIndexMap, this.maxScoreDependency, this.compass);
    this.rangeIdToIndexMap.set(range.id, this.scaleRanges.length);
    this.scaleRanges.push(range);
    if (shouldUpdate)
      this.rangesStatusField?.update();
  }

  removeScaleRange(id: number, shouldUpdate: boolean = true) {
    const index: number = this.rangeIdToIndexMap.get(id);
    if (index == null)
      return;
    this.scaleRanges[index].destroy();
    this.scaleRanges.splice(index, 1);
    for (let i = index; i < this.scaleRanges.length; i++)
      this.rangeIdToIndexMap.set(this.scaleRanges[i].id, i);
    this.rangeIdToIndexMap.delete(id);
    if (shouldUpdate)
      this.rangesStatusField?.update();
  }

  static generateGUID(): string {
    return UUIDService.generateUUID();
  }
}

export class CompassScaleRangeGroup extends SavedGroupStatus implements CompassEditable, FieldMap {
  private static nextId: number = 1;

  compass: SavedCompass;
  rangesDependency: RangeOverlapDependency<LineRange>;
  id: number;
  name: SavedTextField;
  minimum: SavedNumberField;
  maximum: SavedNumberField;
  statusField: SimpleStatusField;
  isNew: boolean;
  fieldMap: FieldMapType;

  constructor(from: CompassScaleRange, parentUpdators: CountUpdators, rangesDependency: RangeOverlapDependency<LineRange>,
    rangeIdToIndexMap: Map<number, number>, maxScoreDependency?: AbstractSumDependency, compass?: SavedCompass) {
    super(parentUpdators);
    this.isNew = from == null;
    this.compass = compass;
    this.rangesDependency = rangesDependency;
    this.id = CompassScaleRangeGroup.nextId++;
    this.statusField = new SimpleStatusField(this.updators, [rangesDependency], [() => {
      const overlappingIds: number[] = rangesDependency.getOverlapping(this.id).sort();
      return overlappingIds.length == 0 ? null : 'Overlaps with range' + (overlappingIds.length == 1 ? ' ' : 's ')
        + overlappingIds.map((rangeId: number, index: number, ranges: number[]): string =>
          ((index > 0 && index == ranges.length - 1) ? 'and ' : '') + (RangeID.fromRangeIndex(rangeIdToIndexMap.get(rangeId))).toString())
          .join(overlappingIds.length > 2 ? ', ' : ' ');
    }]);
    const accessControlDependencies: Dependency[] = this.compass ? [this.compass.accessControl] : [];
    const isEditable: () => boolean = (): boolean => !this.compass || this.compass.isEditing() || this.compass.isEditing(this);
    this.name = new SavedTextField(from ? from.name : null, null, this.updators, [...accessControlDependencies], null, (): boolean => {
      return isEditable() && (!this.compass || this.compass.isOriginal() || !this.compass.accessControl.has(CompassAccessControl.SCORE_SUMMARY_TEXT_EDITING_ALLOWED));
    }, null, true, null, 128);
    this.minimum = new SavedNumberField(from ? from.minimum?.toString() : null, null, this.updators, [...accessControlDependencies],
      [(field: SavedNumberField): string =>
        (field.value != null && this.maximum && this.maximum.value != null && parseInt(field.value) > parseInt(this.maximum.value))
          ? 'Cannot be greater than the maximum' : null,
      ], (): boolean => {
        return isEditable() && (!this.compass || this.compass.isOriginal() || !this.compass.accessControl.has(CompassAccessControl.SCORE_SUMMARY_SCALE_EDITING_ALLOWED));
      }, (oldValue: string, newValue: string): void => {
        if (newValue != oldValue)
          this.updateRangeDependency();
      }, true, 0, null, true
    );
    let maxDependencies: Dependency[] = [this.minimum, ...accessControlDependencies];
    if (maxScoreDependency)
      maxDependencies.push(maxScoreDependency);
    this.maximum = new SavedNumberField(from ? from.maximum?.toString() : null, null, this.updators, maxDependencies,
      [
        (field: SavedNumberField): string =>
          (field.value != null && this.minimum && this.minimum.value != null && parseInt(field.value) < parseInt(this.minimum.value))
            ? 'Cannot be less than the minimum' : null,
        (field: SavedNumberField): string => {
          if (field.value == null || field.value.length == 0)
            return '';
          const value: number = parseInt(field.value);
          return (maxScoreDependency && !isNaN(value) && value > maxScoreDependency.getSum()) ? 'Must not exceed max scale value' : '';
        }
      ], (): boolean => {
        return isEditable() && (!this.compass || this.compass.isOriginal() || !this.compass.accessControl.has(CompassAccessControl.SCORE_SUMMARY_SCALE_EDITING_ALLOWED));
      }, (oldValue: string, newValue: string): void => {
        if (newValue != oldValue)
          this.updateRangeDependency();
      }, true, 0, null, true
    );
    this.minimum.addDependencies(this.maximum);
    this.updateRangeDependency();
    if (!this.isNew)
      this.touchAll(true);
    this.fieldMap = {
      name: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      weight: {
        name: 'name', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      minimum: {
        name: '', handlers: {
          MIN: FieldMapHandlerStrings.DEFAULT
        }
      },
      maximum: {
        name: '', handlers: {
          MIN: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT,
          FMT: FieldMapHandlerStrings.DEFAULT
        }
      }
    };
  }

  getEditId(): number {
    return this.name.getDependencyId();
  }

  destroy() {
    this.rangesDependency.delete(this.id);
    this.detach();
  }

  updateRangeDependency() {
    this.rangesDependency.set(new LineRange(this.id,
      { value: parseInt(this.minimum?.value), inclusive: true }, { value: parseInt(this.maximum?.value), inclusive: true }));
  }

  updateFrom(from: CompassScaleRange) {
    this.isNew = from == null;
    this.name.savedValue = from ? from.name : null;
    this.minimum.savedValue = from ? from.minimum?.toString() : null;
    this.maximum.savedValue = from ? from.maximum?.toString() : null;
    if (!this.isNew)
      this.touchAll(true);
  }

  touchAll(value: boolean = true) {
    this.name.touch(value);
    this.minimum.touch(value);
    this.maximum.touch(value);
    this.statusField.touch(value);
  }

  resetAll() {
    this.name.reset();
    this.minimum.reset();
    this.maximum.reset();
    this.statusField.reset();
    this.statusField?.update();
  }

  getSaved(): CompassScaleRange {
    return new CompassScaleRange({
      name: this.name.value,
      minimum: (this.minimum.value != null && this.minimum.value.length > 0) ? parseInt(this.minimum.value) : null,
      maximum: (this.maximum.value != null && this.maximum.value.length > 0) ? parseInt(this.maximum.value) : null
    });
  }

  touchMinMax(field: SavedNumberField) {
    if (this.minimum.value != null && this.maximum.value != null) {
      this.minimum.touch();
      this.maximum.touch();
    } else if (field.problem != null)
      field.touch();
    this.statusField.touch();
  }
}

export class CompassDocumentsGroup extends SavedGroupStatus implements CompassEditable, FieldMap, Collapsible {
  id: number;
  name: SavedTextField;
  url: SavedTextField;
  authors: SavedTextField;
  description: SavedTextField;
  statusField: SimpleStatusField;
  isNew: boolean;
  compass: SavedCompass;
  fieldMap: FieldMapType;
  expanded: boolean;

  constructor(from: Document, parentUpdators: CountUpdators,
    compass: SavedCompass, forceNew: boolean = false) {
    super(parentUpdators);
    this.expanded = false;
    this.isNew = forceNew || from == null;
    this.compass = compass;
    this.statusField = new SimpleStatusField(this.updators, null, [], null);

    //this.statusField = new SimpleStatusField();
    const isEditable: () => boolean = (): boolean => !this.compass || this.compass.isEditing() || this.compass.isEditing(this);
    this.name = new SavedTextField(from ? from.name : null, null, this.updators, [],
      [], (): boolean => {
        return isEditable() && (!this.compass || this.compass.isOriginal());
      }, null, true, null, 512);
    this.url = new SavedTextField(from ? from.url : null, null, this.updators, [],
      [], (): boolean => {
        return isEditable() && (!this.compass || this.compass.isOriginal());
      }, null, true, null, 1024);
    this.description = new SavedTextField(from ? from.description : null, null, this.updators, [],
      [], (): boolean => {
        return isEditable() && (!this.compass || this.compass.isOriginal());
      }, null, true, null, 1024);
    this.authors = new SavedTextField(from ? from.authors : null, null, this.updators, [],
      [], (): boolean => {
        return isEditable() && (!this.compass || this.compass.isOriginal());
      }, null, true, null, 1024);
    this.fieldMap = {
      name: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      url: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      description: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      },
      authors: {
        name: '', handlers: {
          REQ: FieldMapHandlerStrings.DEFAULT,
          MAX: FieldMapHandlerStrings.DEFAULT
        }
      }
    };
  }



  getEditId(): number {
    return this.name.getDependencyId();
  }



  destroy() {
    if (this.compass?.isEditing(this))
      this.compass.edit(null);
    this.detach();
  }

  changeTo(document: Document) {
    document = document.clone();

    this.updateFrom(document, true);
    this.resetAll();
    this.compass.trackableStatusField?.update();
  }

  updateFrom(from: Document, forceNew?: boolean) {
    this.isNew = forceNew != null ? forceNew : from == null;
    this.id = from ? from.id : null;
    this.name.savedValue = from ? from.name : null;
    this.url.savedValue = from ? from.url : null;
    this.description.savedValue = from ? from.description : null;
    this.authors.savedValue = from ? from.authors : null;
    this.statusField?.update();
    if (!this.isNew)
      this.touchAll(true);
  }

  touchAll(value: boolean = true) {
    this.statusField.touch(value);
    this.name.touch(value);
    this.authors.touch(value);
    this.description.touch(value);
    this.url.touch(value);
  }

  resetAll() {
    this.name.reset();
    this.authors.reset();
    this.url.reset();
    this.description.reset();
    this.statusField.reset();
    this.statusField?.update();
  }

  getSaved(): Document {
    return new Document({
      id: this.id,
      name: this.name.value,
      description: this.description.value,
      url: this.url.value,
      authors: this.authors.value
    });
  }



}